/**
 * The walkable outpost interior (Onda 6) — a self-contained 3D port scene that reuses the shared
 * WebGLRenderer but owns its own THREE.Scene + camera. Captains walk the plaza as animated avatars
 * (the class-specific captain GLBs, idle/walk/emote clips). The LOCAL avatar is predicted (see
 * portprediction.ts); remotes are interpolated (net.getInterpAvatars). Style: Sea-of-Thieves / Bilgewater
 * — chunky, warm-lit, never realistic.
 *
 * Coordinate convention matches the sea: port-local (x, y) → three.js world (x, 0, y). Ground is y=0.
 */
import * as THREE from 'three';
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { EMOTE_CLIPS, PORT, PORT_LAYOUT, CASINO, CasinoGame, WALK, ShipClassId, NAME_BADGE, AVATAR_SKINS, LEGACY_PROC_KINDS, type PortLayout, type PortProp, type PortStation } from '@piratas/protocol';
import { mulberry32, nearestPortSeat, type CityProp, type SeatSpot } from '@piratas/sim';
import { emoteLoops, isEmoteToggleOff, emoteUntil as emoteUntilOf, shouldEndEmote } from './emotefsm.js';
import { cityPaletteAt, cityPhase, type CityPalette } from './citytime.js';
import { DEALER_CLIP_IDS, pickDealerLine, planDealers, type DealerVoice, type DealerLineKind } from './dealerlines.js';
import type { Audio } from './audio.js';
import type { Renderer } from './render.js';

/** Onda 6.5 — the modular Capital kit: each `PortProp.kind` maps to a GLB the client instances DENSE.
 *  Reuses the tavern / storage / market_stall GLBs already shipped for the village + outposts.
 *  Onda Portos Livres — EXPORTED so the sea renderer (render.ts) can lazily bake+instance the SAME kit GLBs
 *  when an exterior port layout places one outside the walls (single source of truth for the kit URLs). */
export const KIT_URL: Record<string, string> = {
  gate:       '/models/capital_gate/capital_gate.opt.glb',
  lighthouse: '/models/lighthouse/lighthouse.opt.glb',
  wall:       '/models/wall_segment/wall_segment.opt.glb',
  house_a:    '/models/house_a/house_a.opt.glb',
  house_b:    '/models/house_b/house_b.opt.glb',
  fountain:   '/models/fountain/fountain.opt.glb',
  crane:      '/models/dock_crane/dock_crane.opt.glb',
  warehouse:  '/models/storage/storage.opt.glb',
  tavern:     '/models/tavern/tavern.opt.glb',
  stall:      '/models/market_stall/market_stall.opt.glb',
  // Onda 7 — the casino wing + physical auction rostrum + clan-hall trophies
  poker_table:     '/models/poker_table/poker_table.opt.glb',
  slot_machine:    '/models/slot_machine/slot_machine.opt.glb',
  blackjack_table: '/models/blackjack_table/blackjack_table.opt.glb',
  auction_stage:   '/models/auction_stage/auction_stage.opt.glb',
  clan_trophy:     '/models/clan_trophy/clan_trophy.opt.glb',
  // Onda Cidade Viva — the owner's Meshy street-dressing kit (bench / lamp / cottage / lantern grove)
  cobble_bench:    '/models/props/cobble_bench.opt.glb',
  street_lamp:     '/models/props/street_lamp.opt.glb',
  cottage_ember:   '/models/props/cottage_ember.opt.glb',
  lantern_grove:   '/models/props/lantern_grove.opt.glb',
  // Onda Cidade Verde — the landscape kit as CITY props (the same GLBs the sea-view Landmark Editor plants):
  // dense conifers + a stackable cliff chunk, now plantable from inside the city (?editor=1). `rock` is the
  // 4th landscape kind but has NO GLB — it's built procedurally at load (see buildProcRock), so it is absent
  // here and injected into kitBaked in preloadCapital.
  pine:            '/models/pine/pine.opt.glb',
  pine_tall:       '/models/pine_tall/pine_tall.opt.glb',
  cliff:           '/models/cliff/cliff.opt.glb',
  // Onda Skull Harbor — the owner's Meshy harbour kit: pier-diorama buildings for the redesigned first village
  // (VILLAGE1_LAYOUT) + the capital citadel. Detailed Meshy dioramas decimated to ~40-73k tris (buildings) /
  // ~16k (tree) and webp-1024'd; forced DoubleSide (see SKULL_HARBOR_KINDS) so the decimated shells never
  // read see-through. tavern/inn/wharf/tree compose the village; the citadel is a capital-only palette piece
  // the owner positions himself (never placed by default).
  tavern_skull:    '/models/tavern_skull/tavern_skull.opt.glb',
  inn_salted:      '/models/inn_salted/inn_salted.opt.glb',
  wharf_skullflag: '/models/wharf_skullflag/wharf_skullflag.opt.glb',
  treasure_tree:   '/models/treasure_tree/treasure_tree.opt.glb',
  citadel_skull:   '/models/citadel_skull/citadel_skull.opt.glb',
};
/** Onda Skull Harbor — the owner's harbour-kit kinds. Meshy bakes single-sided materials and the aggressive
 *  decimation leaves the odd open edge, so (like LANDSCAPE_KINDS) their baked template is forced DoubleSide. */
export const SKULL_HARBOR_KINDS = new Set(['tavern_skull', 'inn_salted', 'wharf_skullflag', 'treasure_tree', 'citadel_skull']);
/** Hero-building LOD — the heavy harbour dioramas (100-400k tris) the owner uses as port façades ship a decimated
 *  `<name>.lod.glb` (~20% tris) beside the `.opt.glb`. The sea view (render.ts) and the walkable port both swap the
 *  LOD in past ~400 m and the full mesh within ~390 m (30 m hysteresis). SINGLE SOURCE — render.ts imports this.
 *  Derive the LOD url from KIT_URL by swapping `.opt.glb`→`.lod.glb`. */
export const HEAVY_LOD_KINDS: ReadonlySet<string> = SKULL_HARBOR_KINDS;
/** Walkable-port LOD swap distances (m², from the follow camera to a prop cluster centroid); 30 m hysteresis. */
const PORT_LOD_FAR_D2 = 420 * 420;
const PORT_LOD_NEAR_D2 = 390 * 390;
/** Onda Cidade Verde — the landscape kinds. Meshy bakes single-sided materials, so their baked template gets
 *  DoubleSide forced (a tree's foliage / a thin cliff face must read from every camera angle — the same lesson
 *  the sea-view landmark loader applies). `rock` is procedural (built DoubleSide already). */
const LANDSCAPE_KINDS = new Set(['pine', 'pine_tall', 'rock', 'cliff']);
/** Target FOOTPRINT (m, horizontal) per kit kind — tall pieces scale by HEIGHT instead (KIT_TALL). The Meshy
 *  props ship "tabletop" (~1-2 m), so each is up-scaled here to human-city proportion: a ~2 m bench (native size,
 *  seat ~0.48 m for a 1.8 m avatar), a ~5.5 m one-storey cottage. The slender lamp + the tree-in-a-planter scale
 *  by height (KIT_TALL) so the post reads ~4.6 m of street lamp and the grove ~4.2 m tall, not squashed flat. */
export const KIT_FOOT: Record<string, number> = {
  gate: 13, lighthouse: 9, wall: 12, house_a: 8, house_b: 9, fountain: 8, crane: 6.5, warehouse: 8.5, tavern: 14, stall: 3.4,
  poker_table: 5, blackjack_table: 5, auction_stage: 4.4, clan_trophy: 1.7,
  cobble_bench: 2.0, cottage_ember: 5.5,
  // the tavern stool: its footprint IS the 0.35 m turned seat disc (built native, see buildProcStool), so a foot
  // of 0.35 renders it at exactly native size — seat top lands at ~0.45 m, matching CITY_SEAT.stool.y0.
  stool: 0.35,
  // Onda Cidade Verde — footprint-normalized landscape: a low ~1-2 m boulder and the "peça grande" cliff chunk
  // (~6 m across). Trees scale by HEIGHT instead (KIT_TALL) so they read tall, not squashed flat.
  rock: 2.4, cliff: 6,
  // Onda Skull Harbor — the harbour buildings scale by FOOTPRINT (pier-diorama base): a ~14 m tavern platform
  // (house ~7 m), an ~11 m two-storey inn, a ~13 m wharf, and the imposing ~38 m sprawling citadel harbour
  // complex (its pagoda crown reads ~14 m). The treasure tree scales by HEIGHT instead (KIT_TALL) so it reads
  // as a tall landmark, not a squashed islet.
  tavern_skull: 14, inn_salted: 11, wharf_skullflag: 13, citadel_skull: 38,
  // Onda Sombras — a ~5 m default ground-shadow decal; the owner dials it to fit each prop via the per-prop `s`.
  shadow_round: 5, shadow_square: 5,
};
/** Kinds scaled to a target HEIGHT (m) so slender towers read tall — not squashed to a footprint. */
export const KIT_TALL: Record<string, number> = {
  lighthouse: 22, gate: 12, slot_machine: 2.4, street_lamp: 4.6, lantern_grove: 4.2,
  // Onda Cidade Verde — urban conifers: a stout ~8 m spruce and a clearly-taller ~11 m fir silhouette.
  pine: 8, pine_tall: 11,
  // Onda Skull Harbor — the treasure tree reads ~9 m tall (its sand/water islet base scales with it) so it
  // crowns the village plaza as a landmark, not a squashed footprint.
  treasure_tree: 9,
};
/** Onda Sombras — ground-shadow decal kinds. NO GLB, NO KIT_TALL: each is a flat alpha plane laid on the floor,
 *  footprint-scaled (KIT_FOOT) then multiplied by the per-prop `s` so the owner fits it under any house/prop.
 *  Baked in doPreloadCapital from a soft webp alpha mask; rendered UNLIT with depthWrite off + polygonOffset +
 *  a high renderOrder so it always draws on top of the plaza floor without z-fighting and never occludes the
 *  prop above it. NO collision (deliberately absent from the editor's DECO_KINDS → seeds no walker obstacle). */
export const SHADOW_KINDS: ReadonlySet<string> = new Set(['shadow_round', 'shadow_square']);
const SHADOW_TEX: Record<string, string> = {
  shadow_round: '/textures/shadow_round.webp',
  shadow_square: '/textures/shadow_square.webp',
};
/** World-Y lift (m) of a shadow decal above the plaza floor — clears the render-only ground patches (y≈0.03)
 *  so the decal always draws over them; belt-and-suspenders with the material's depthWrite:false + polygonOffset. */
const SHADOW_LIFT = 0.06;
/** Onda Cidade Viva — kinds whose lamp "acende de noite": ONE instanced emissive glow head per kind (same
 *  pattern + drive as the city windows, ≤ +1 draw call each), lit warm at night and dark by day via
 *  applyCityTime. `headY` is the world-Y (m) of the flame head after the kind's fixed KIT_TALL scale. */
export const LAMP_KINDS: Record<string, { headY: number; color: number; size: number }> = {
  street_lamp:   { headY: 4.1, color: 0xffd591, size: 0.30 },
  lantern_grove: { headY: 2.6, color: 0xffd591, size: 0.26 },
};
/** Onda Cidade Noturna (generalizada) — the lit-window/lantern quads a dwelling gets at night, authored PER
 *  PROP in the prop's LOCAL frame (dx = local east, dz = local north, before the prop's `-heading` yaw; `up` =
 *  world-Y of the pane centre; `ry` = the pane's local facing yaw so its +z normal points OUT of the facade;
 *  `w`×`h` = pane size in m). buildCityWindows rotates each pane by the prop's heading + translates to (x,y),
 *  so ANY house/tavern/cottage the owner drops — anywhere, any rotation, in any editor — glows automatically.
 *  The shipped capital's houses/tavern carry headings that already face the street, so these local specs
 *  reproduce the hand-authored shipped windows exactly (byte-identical by day and night). */
export const WINDOW_SPECS: Record<string, { dx: number; dz: number; up: number; ry: number; w: number; h: number }[]> = {
  house_a: [{ dx: 3.9, dz: -1.5, up: 3.0, ry: Math.PI / 2, w: 1.2, h: 1.5 }, { dx: 3.9, dz: 1.5, up: 3.0, ry: Math.PI / 2, w: 1.2, h: 1.5 }],
  house_b: [{ dx: 3.9, dz: -1.5, up: 3.0, ry: Math.PI / 2, w: 1.2, h: 1.5 }, { dx: 3.9, dz: 1.5, up: 3.0, ry: Math.PI / 2, w: 1.2, h: 1.5 }],
  tavern:  [{ dx: -2.4, dz: -6.9, up: 4.4, ry: Math.PI, w: 1.2, h: 1.5 }, { dx: 2.4, dz: -6.9, up: 4.4, ry: Math.PI, w: 1.2, h: 1.5 }],
  // the owner's front-porch cottage: one warm lantern pane at its front (local +z), so its lantern lights at night.
  cottage_ember: [{ dx: 0, dz: 2.7, up: 2.0, ry: 0, w: 0.6, h: 0.85 }],
  // Onda Skull Harbor — the harbour buildings light their windows at night. Specs are in FINAL WORLD METRES
  // (buildCityWindows reads dx/dz/up as metres off the placed prop), authored on the south face (local -z,
  // the plaza-facing side the captain approaches from — every VILLAGE1 building carries heading 0/±π so the
  // facade faces the street). Warm panes near each building's core (not the pier edge).
  tavern_skull: [{ dx: -2.2, dz: -3.2, up: 3.4, ry: Math.PI, w: 1.1, h: 1.4 }, { dx: 2.2, dz: -3.2, up: 3.4, ry: Math.PI, w: 1.1, h: 1.4 }],
  inn_salted:   [{ dx: -2.0, dz: -2.6, up: 3.0, ry: Math.PI, w: 1.0, h: 1.2 }, { dx: 2.0, dz: -2.6, up: 3.0, ry: Math.PI, w: 1.0, h: 1.2 }, { dx: 0, dz: -2.6, up: 6.2, ry: Math.PI, w: 1.0, h: 1.2 }],
  // the capital citadel — many lit windows across its central pagoda for an epic night silhouette.
  citadel_skull: [
    { dx: -4, dz: -5.0, up: 8, ry: Math.PI, w: 1.4, h: 1.8 }, { dx: 0, dz: -5.5, up: 8, ry: Math.PI, w: 1.6, h: 2.0 }, { dx: 4, dz: -5.0, up: 8, ry: Math.PI, w: 1.4, h: 1.8 },
    { dx: -4, dz: -5.0, up: 4, ry: Math.PI, w: 1.2, h: 1.5 }, { dx: 4, dz: -5.0, up: 4, ry: Math.PI, w: 1.2, h: 1.5 },
  ],
};
/** Every kit kind the client can instance (Onda 6.5/7). The Creative-Mode Layout Editor catalog reads THIS.
 *  `rock` + `stool` are procedural (no GLB in KIT_URL — built by buildProcRock / buildProcStool), so each is
 *  spliced in beside its kin: `rock` with the Cidade Verde landscape kinds, `stool` right after the cobble bench
 *  so the catalog groups the street-dressing seats together. */
export const KIT_KINDS: readonly string[] = (() => {
  const ks = Object.keys(KIT_URL);
  const ip = ks.indexOf('pine_tall');
  if (ip >= 0) ks.splice(ip + 1, 0, 'rock'); else ks.push('rock');
  const ib = ks.indexOf('cobble_bench');
  if (ib >= 0) ks.splice(ib + 1, 0, 'stool'); else ks.push('stool');
  return ks;
})();
/** Layout Editor wave: ground-patch materials by GroundMat id. All six now use real tileable webp textures
 *  (cobble/street/planks + dirt/grass/sand generated in the World Editor wave). GROUND_TINT is a colour
 *  fallback only if a texture ever fails to load. */
const GROUND_TEX: Record<number, string> = {
  0: '/textures/cobblestone.webp', 1: '/textures/street.webp', 2: '/textures/dock_planks.webp',
  3: '/textures/dirt.webp', 4: '/textures/grass.webp', 5: '/textures/sand.webp',
};
const GROUND_TINT: Record<number, number> = { 3: 0x6f5636, 4: 0x4c6b34, 5: 0xcbb487 };
const GROUND_TILE = 6; // metres of ground covered by one texture tile
const UP = new THREE.Vector3(0, 1, 0);
interface KitBaked { geo: THREE.BufferGeometry; mat: THREE.Material | THREE.Material[]; box: THREE.Box3; }

const CAPTAIN_MODELS: Record<number, string> = {
  [ShipClassId.SLOOP]:      '/models/avatar_test/captain_sloop_anim.opt.glb',
  [ShipClassId.BRIGANTINE]: '/models/avatar_test/captain_brigantine_anim.opt.glb',
  [ShipClassId.GALLEON]:    '/models/avatar_test/captain_galleon_anim.opt.glb',
  [ShipClassId.FRIGATE]:    '/models/avatar_test/captain_frigate_anim.opt.glb',
  // Onda Frota (v47): the 3 new hulls REUSE existing on-foot captain looks — cutter→sloop, fluyt→brig, east→galleon.
  [ShipClassId.CUTTER]:        '/models/avatar_test/captain_sloop_anim.opt.glb',
  [ShipClassId.FLUYT]:         '/models/avatar_test/captain_brigantine_anim.opt.glb',
  [ShipClassId.EAST_INDIAMAN]: '/models/avatar_test/captain_galleon_anim.opt.glb',
};
const STALL_MODEL = '/models/market_stall/market_stall.opt.glb';
const AVATAR_HEIGHT = 1.8;     // metres — captains are normalized to this
/** Yaw so the model's nose points along its heading (+x = east): the captain GLBs face local +Z, so
 *  rotation.y = π/2 − heading turns that forward onto the heading direction (back to the trailing camera). */
export const MODEL_YAW = Math.PI / 2;
/** Fix 11 (walk feel): the ground distance (m) the `walk` clip covers per loop at timeScale 1. The client
 *  scales the clip's playback to the avatar's MEASURED speed so the feet track the ground (no foot-sliding).
 *  Tuned so a full-speed stroll (WALK.SPEED) reads as a brisk, natural walk — purely a visual layer. */
const WALK_CLIP_STRIDE = 2.2;

/* ------------------------------- Onda NPC Mercante ------------------------------- */
/** A market NPC per city — 100% client-side scenery (like torches/props): NOT a network entity, never in the
 *  port population or the walker sim. It reuses a rigged captain GLB with a merchant recolor + a floating name
 *  tag, anchored at the layout's market stall. All variation is DETERMINISTIC from the outpost id (seeded
 *  mulberry32 — never Math.random), so every client sees the same merchant at the same city. */
const MERCHANT_NAMES: readonly string[] = [
  'Old Mango Marley', 'Coinpurse Kate', 'Barnacle Bill', 'Doubloon Dory', 'Salty Sam Sloan',
  'Gilda Goldtooth', 'Copper-Penny Nel', 'Trader Tobias', 'Peg-Leg Pearl', 'Molasses Meg',
  'Silver-Tongue Sy', 'Cutlass Cyrus',
];
/** Earthy merchant/apron tints the captain outfit is recolored toward (one picked per port by seed). */
const MERCHANT_TINTS: readonly number[] = [
  0x8a6a44, 0x6b5a3a, 0x7a5540, 0x556b4a, 0x86794f, 0x6e4a3a, 0x4f5b6b, 0x7c6b8a,
];
/** How strongly the recolor pulls each captain material toward its tint (0 = keep, 1 = replace). */
const MERCHANT_TINT_MIX = 0.68;
/** The merchant turns to face a captain who steps within this (m); otherwise it faces the plaza flow. */
const MERCHANT_NOTICE_R = 6;

/* ------------------------------- Onda Dealers ------------------------------- */
/** A croupier NPC behind each blackjack + poker table of the APPLIED layout — 100% client-side scenery, exactly
 *  like the market merchant (never a network entity, never in the walker sim). All variation (name/tint/base
 *  captain) is DETERMINISTIC from the outpost + station id, so every client sees the same dealer at the same
 *  table. It idles, turns to face the nearest captain, and throws the odd deadpan line (balloon + a pre-generated
 *  voice clip) when a captain is at the felt or on a big table event. */
/** Gambler-black waistcoat tints the croupier outfit is recolored toward (picked per table by seed). */
const DEALER_TINTS: readonly number[] = [0x2a2622, 0x1f2430, 0x2e2230, 0x24302a, 0x30281f];
const DEALER_TINT_MIX = 0.72;
/** The dealer turns to face a captain within this (m) — a touch tighter than the merchant (they mind their felt). */
const DEALER_NOTICE_R = 5;
/** A dealer never speaks more than once per this (ms) — the owner's hard "1 fala a cada 20 s por mesa" cap. */
const DEALER_LINE_MIN_MS = 20000;
/** Idle-mutter cadence while a captain is at the felt (ms): a fresh interval rolled after each line. */
const DEALER_IDLE_MIN_MS = 20000, DEALER_IDLE_MAX_MS = 45000;
/** How long a spoken balloon lingers above the dealer's head (ms). */
const DEALER_BUBBLE_MS = 4200;

type GltfLoader = { loadAsync(url: string): Promise<{ scene: THREE.Object3D; animations?: THREE.AnimationClip[] }>; setMeshoptDecoder(d: unknown): void };

interface Template { scene: THREE.Object3D; clips: THREE.AnimationClip[]; scale: number; }
interface Avatar {
  group: THREE.Group;
  mixer: THREE.AnimationMixer;
  actions: Map<string, THREE.AnimationAction>;
  current: string;         // the looping locomotion clip currently faded in
  emote: string | null;    // a one-shot emote clip playing, or null
  emoteUntil: number;      // wall-clock ms the emote ends
  cls: number;
  /** Onda Avatares: the cosmetic skin (AVATAR_SKINS id) this avatar was built for — a change (live re-skin,
   *  cached from a re-emitted SPAWN) triggers a model swap in ensureAvatar. 0 = the default per-hull captain. */
  skin: number;
  /** True until the wanted skin's GLB has actually landed (skin>0 built off the fallback cls captain); the
   *  preload tail swaps it for the real skin the instant the template streams in. */
  skinReady: boolean;
  tag: THREE.Sprite | null;
  // Fix 11 — visual animation state (never touches the deterministic walker sim):
  visYaw: number;          // eased render yaw (approaches MODEL_YAW - heading) → smooth MMO-style turning
  lastX: number;           // previous frame position, to measure real ground speed for foot-sync
  lastY: number;
  hasLast: boolean;
  procedural: boolean;     // built from the capsule fallback (templates weren't loaded yet) → reskin on load
  sitting: boolean;        // Onda 8 Interior Vivo: held seated pose (v43: SYNCED — self + remotes both hold it)
  sitY: number;            // seat-surface height (m) the seated avatar is lifted to
  /** v43 Synced Sit: for a REMOTE seated captain, the server-derived seat transform its pose is snapped to
   *  (its interpolated walker position drifts off the bench, so the pose reads off THIS instead). null = use
   *  the passed transform (the local sitter, already snapped upstream in sync). */
  seatPose: { x: number; y: number; heading: number } | null;
}

/** Onda NPC Mercante — the deterministic market NPC held for the active port (client-only, no wire). */
interface Merchant {
  root: THREE.Group;            // positioned at the anchor; never rotated as a whole
  avatarPivot: THREE.Group;     // the recolored captain — this is what eases its yaw to face a customer
  wares: THREE.Group;           // the merchant's cheap procedural crate/barrel/sack (own geo+mats)
  mixer: THREE.AnimationMixer;
  tag: THREE.Sprite;
  mats: THREE.Material[];        // the CLONED captain materials (tinted) — the only captain resources we own
  name: string;
  stallIdx: number;             // the layout stall it tends (drives the diegetic trade prompt)
  homeX: number; homeY: number; // anchor, port-local
  baseYaw: number;              // sim-plane heading it faces when idle (toward the stall's customer spot)
  visYaw: number;               // eased render yaw (rotation.y of avatarPivot)
}

/** Onda Dealers — a casino croupier NPC held for one blackjack/poker table (client-only, no wire). */
interface Dealer {
  root: THREE.Group;            // positioned behind the table; never rotated as a whole
  avatarPivot: THREE.Group;     // the recolored captain — eases its yaw to face a nearby captain
  mixer: THREE.AnimationMixer;
  nameTag: THREE.Sprite;        // the floating "🎲 Name — Dealer" label
  bubble: THREE.Sprite;         // the speech balloon (hidden until a line fires)
  mats: THREE.Material[];        // the CLONED captain materials (tinted) — the only captain resources we own
  name: string;
  voice: DealerVoice;           // 'bj' | 'poker' — the pit it tends AND the TTS voice it draws lines with
  stationId: number;            // the casino station it stands behind
  homeX: number; homeY: number; // anchor, port-local (behind the table)
  worldX: number; worldZ: number; // three.js world position (for cheap positional-audio pan)
  baseYaw: number;              // sim heading facing the felt / the seated captain
  visYaw: number;
  rand: () => number;           // the dealer's own seeded PRNG (line pick + idle interval — never Math.random)
  nextIdleAt: number;           // wall-clock ms the next idle mutter may fire (while a captain is at the felt)
  lastLineAt: number;           // wall-clock ms of the last line (enforces the 1/20 s-per-table cap)
  bubbleUntil: number;          // wall-clock ms the balloon hides again
}

export class PortScene {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  protected webgl: THREE.WebGLRenderer; // shared WebGLRenderer (HouseScene's ExteriorVista renders an offscreen portal pass on it)
  private templates = new Map<number, Template>();
  /** Onda Avatares: cosmetic captain looks keyed by AVATAR_SKINS id (1=lady, 2=skeleton). Loaded alongside
   *  the class captains; an avatar with skin>0 uses one of these instead of its per-hull captain. */
  private skinTemplates = new Map<number, Template>();
  private stallTemplate: THREE.Object3D | null = null;
  private avatars = new Map<number, Avatar>();
  /** Onda NPC Mercante — the city's market NPC (client-only). Deferred until the captain GLBs stream in. */
  private merchant: Merchant | null = null;
  private merchantOutpost = -1;
  /** Onda Dealers — one croupier per blackjack/poker table of the applied layout (client-only). Deferred like
   *  the merchant until the captain templates exist. `audio` is threaded in from main.ts for the voice clips. */
  private dealers: Dealer[] = [];
  private dealersOutpost = -1;
  private pendingDealersOutpost: number | null = null;
  private audio: Audio | null = null;
  private pendingMerchantOutpost: number | null = null;
  private loadStarted = false;
  private _loaded = false;
  protected camTarget = new THREE.Vector3(PORT_LAYOUT.spawn.x, 1.2, PORT_LAYOUT.spawn.y);
  protected camPos = new THREE.Vector3(PORT_LAYOUT.spawn.x, 6, PORT_LAYOUT.spawn.y + 8);
  private torchMats: THREE.MeshStandardMaterial[] = [];
  /** Onda de-proc: torch placements collected during a build, then baked into ONE instanced iron-tripod
   *  brazier + ONE instanced flame (2 draw calls for every torch in the scene). */
  private torchPlaces: { x: number; z: number; y: number }[] = [];
  private torchGroup: THREE.Group | null = null;
  /** Onda Cidade Noturna — the scene's GLOBAL lights (kept as refs so the day/night cycle can retint them each
   *  frame instead of adding any per-prop light). Reassigned on every (re)build; null in the house shell. */
  private hemiLight: THREE.HemisphereLight | null = null;
  private sunLight: THREE.DirectionalLight | null = null;
  private ambLight: THREE.AmbientLight | null = null;
  /** The warm point light under every torch — boosted a touch at night so the braziers own the dark. */
  private torchLights: THREE.PointLight[] = [];
  /** Emissive materials for lit windows (the tavern's panes + the capital's house-facade window quads). Their
   *  emissiveIntensity is driven 0 (day) → warm (night) by the cycle, exactly like the house's stern windows. */
  private windowMats: THREE.MeshStandardMaterial[] = [];
  /** The ONE instanced mesh of emissive "lit window" quads on the capital's house facades (~1 draw call). */
  private cityWindowsGroup: THREE.Group | null = null;
  /** Onda Cidade Viva — the group holding ONE instanced emissive glow head per lamp kind (street lamp / lantern
   *  grove). Their materials are pushed into windowMats so applyCityTime lights them at night alongside the
   *  windows. Rebuilt on every capital (re)build; null in the village + house (no props there). */
  private cityLampsGroup: THREE.Group | null = null;
  /** Onda Cidade Viva — local seated state on a PORT bench (a city seat prop). v43: the LOCAL sit is mirrored
   *  over the wire via onSitWire (below); the seated-snap block in sync is still inert inside a house (HouseScene
   *  keeps its OWN sit state). */
  private _portSit = false;
  private _portSeat: SeatSpot | null = null;
  private _portSitFrom: { x: number; y: number } | null = null;
  private _sitYourId = -1;
  /** Onda Sit-Âncora — the editor's live Seat preview: pin the ghost into a configured seat, bypassing
   *  nearestPortSeat + the walk-away auto-stand (both inert in the editor). Null in-game. Set via editorPreviewSit.
   *  Protected so HouseScene (which has its OWN sync override + standUp cleanup) can drive the same field. */
  protected _editorSit: SeatSpot | null = null;
  /** v43 Synced Sit: fired whenever the LOCAL captain sits (true) or stands (false) — including the auto-stand
   *  when they walk off the bench. main.ts wires this to net.sendPort(PortOp.SIT, …) so the server re-broadcasts
   *  the pose. Inherited by HouseScene, which drives the house Sit through the same hook. */
  onSitWire?: (on: boolean) => void;
  /** Base fog near/far captured at build time so night can pull the near haze in without forgetting daylight. */
  private baseFogNear = 60; private baseFogFar = 260;
  /** Onda Cidade Noturna — pin the city time-of-day (render-only, dev/E2E hook via __cc). null = live cycle. */
  private phaseOverride: number | null = null;
  /** Per-frame palette scratch (allocation-free retint — filled by cityPaletteAt each frame). */
  private _cityPal: CityPalette = cityPaletteAt(0.5);
  /** Onda 6.5: all static scenery lives here so switching layout (village ↔ capital) can rebuild cleanly.
   *  (protected so HouseScene — Onda 8 — can build the cabin interior into it). */
  protected readonly staticRoot = new THREE.Group();
  protected layout: PortLayout = PORT_LAYOUT;
  private isCapital = false;
  /** Creative-Mode Layout Editor: when set, the scene keeps its environment but instances NO kit props —
   *  the editor owns an individually-selectable prop layer instead. Never true in the normal game. */
  private editorMode = false;
  /** Baked kit geometry per kind (Onda 6.5) — merged once on load, instanced per placement. */
  protected kitBaked = new Map<string, KitBaked>();
  /** Hero-building LOD — the decimated `.lod.glb` bake per heavy kind, loaded beside the full mesh so the walkable
   *  port can swap it in past ~400 m (an owner using a citadel as a distant backdrop). Placed with the FULL bake's
   *  box so the two are pixel-aligned. Absent → the full mesh is simply always used for that kind. */
  private kitLodBaked = new Map<string, KitBaked>();
  /** Hero-building LOD — the full/lod InstancedMesh pairs on the plaza, swapped by camera distance (30 m
   *  hysteresis) each frame. `cx,cz` is the kind's placement centroid. Rebuilt with the static world. */
  private portLodPairs: { full: THREE.InstancedMesh; lod: THREE.InstancedMesh; cx: number; cz: number; far: boolean }[] = [];
  private kitLoadPromise: Promise<void> | null = null; // shared so every awaiter sees the kit FULLY baked
  private kitReady = false;
  private kitRetried = new Set<string>(); // bug #1: kit kinds whose failed bake we've already retried once (bounded)
  /** Onda de-proc: tileable ground textures (cobble/flagstone/planks), keyed by url+repeat and shared
   *  across village↔capital rebuilds so switching layout never re-decodes or leaks a GPU texture. */
  private texLoader = new THREE.TextureLoader();
  private texCache = new Map<string, THREE.Texture>();
  /** Onda 36 walker fix: the camera's own WORLD azimuth (its ground-forward direction), DECOUPLED from the
   *  avatar heading. Walk input is camera-relative to THIS, and the follow cam trails from behind THIS — so
   *  turning the avatar to face its movement no longer rotates the camera. The old scheme placed the camera
   *  behind `local.heading` while the walk input was taken relative to the camera AND the heading eased toward
   *  the input: three coupled feedbacks that spun the avatar into a spiral on ANY off-axis (diagonal / thumb-
   *  stick) input — "impossible to walk" on mobile. `camYaw` only changes when the PLAYER drags to orbit; it
   *  never chases the heading, so the loop is broken and a straight push walks a straight line. */
  protected camYaw = Math.PI / 2; // default = looking north up the street (matches the reveal + spawn facing)
  private orbitActive = false;
  private orbitId = -1;
  private orbitLastX = 0;
  /** LIGHT MODE (wallet in-app browser / low-RAM phone): trims the port's own footprint — the hero-building LOD
   *  swap is biased to always show the decimated `.lod.glb`, shaving triangles that a WKWebView can't afford. */
  private lowMem = false;

  constructor(renderer: Renderer) {
    this.webgl = renderer.renderer;
    this.camera = new THREE.PerspectiveCamera(60, window.innerWidth / Math.max(1, window.innerHeight), 0.1, 900);
    this.scene.add(this.staticRoot);
    this.buildStaticWorld();
    this.bindOrbit(this.webgl.domElement);
  }

  /** Enable/disable camera-orbit drag — main.ts turns it on only for whichever walkable scene is live, so the
   *  port and house instances (both bound to the shared canvas) never both react, and the sea keeps its own. */
  setOrbitActive(on: boolean): void { this.orbitActive = on; if (!on) this.orbitId = -1; }

  /** LIGHT MODE toggle — mirrors the sea renderer's low-memory profile into the walkable port (heavy-building
   *  LOD forced to the decimated mesh). Render-only; set once at boot on a wallet webview / low-RAM phone. */
  setLowMemory(on: boolean): void { this.lowMem = on; }

  /** Rotate the view azimuth by `dYaw` radians. Decoupled from the avatar heading, so it can never feed back
   *  into the walk direction — the camera stays exactly where the player leaves it. */
  orbitBy(dYaw: number): void {
    let y = this.camYaw + dYaw;
    while (y > Math.PI) y -= 2 * Math.PI;
    while (y < -Math.PI) y += 2 * Math.PI;
    this.camYaw = y;
  }

  /** One-finger / mouse drag on the empty view orbits the camera (WoW/L2 convention: drag right → view swings
   *  right). The HUD thumbstick + buttons live in a separate DOM layer, so their pointers never reach the canvas
   *  here. Gated by `orbitActive` so only the live walkable scene reacts. */
  private bindOrbit(el: HTMLElement): void {
    const RATE = 0.0055; // rad per CSS pixel — a comfortable ~half-turn across a phone width
    el.addEventListener('pointerdown', (e) => { if (!this.orbitActive) return; this.orbitId = e.pointerId; this.orbitLastX = e.clientX; });
    el.addEventListener('pointermove', (e) => {
      if (!this.orbitActive || e.pointerId !== this.orbitId) return;
      const dx = e.clientX - this.orbitLastX; this.orbitLastX = e.clientX;
      this.orbitBy(-dx * RATE); // drag right (dx > 0) swings the view right
    });
    const end = (e: PointerEvent) => { if (e.pointerId === this.orbitId) this.orbitId = -1; };
    el.addEventListener('pointerup', end); el.addEventListener('pointercancel', end);
  }

  get loaded(): boolean { return this._loaded; }

  /** Onda 6.5: point the scene at the layout of the port just entered (the dense Capital or a village).
   *  Rebuilds the static world when the layout changes and re-frames the entry camera up the main street. */
  setLayout(layout: PortLayout, isCapital: boolean): void {
    if (this.layout === layout && this.isCapital === isCapital) return;
    this.layout = layout;
    this.isCapital = isCapital;
    this.disposeStatic();
    // frame the reveal: camera at the dock, looking up the street toward the plaza
    const sp = layout.spawn, look = layout.camLook ?? { x: sp.x, y: sp.y + 10 };
    const camBackZ = sp.y - (isCapital ? 11 : 8);
    this.camPos.set(sp.x, isCapital ? 7 : 6, camBackZ);
    this.camTarget.set(look.x, 1.4, look.y);
    // seed the (decoupled) view azimuth to the reveal's look direction so movement + follow start framed up the street
    this.camYaw = Math.atan2(look.y - camBackZ, look.x - sp.x);
    this.buildStaticWorld();
    if (isCapital && this.kitReady) this.placeCapitalKit();
  }

  /** Free every static mesh/material/geometry + torch so setLayout can rebuild for a different layout.
   *  Kit instanced meshes (userData.keepGeo) share the cached baked geometry/material — remove but don't
   *  dispose them, so re-entering the Capital doesn't need a re-bake. */
  protected disposeStatic(): void {
    for (const child of [...this.staticRoot.children]) {
      this.staticRoot.remove(child);
      child.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.isMesh && !m.userData.keepGeo) { m.geometry?.dispose?.(); const mm = m.material; if (Array.isArray(mm)) mm.forEach((x) => x.dispose?.()); else mm?.dispose?.(); }
      });
    }
    this.torchMats.length = 0;
    this.torchPlaces.length = 0; this.torchGroup = null;
    // Onda Cidade Noturna — the global lights, torch lights + window mats belonged to the disposed staticRoot.
    this.hemiLight = null; this.sunLight = null; this.ambLight = null;
    this.torchLights.length = 0; this.windowMats.length = 0; this.cityWindowsGroup = null; this.cityLampsGroup = null;
    this.portLodPairs.length = 0; // hero-LOD pairs were children of the disposed staticRoot; rebuilt on next build
  }

  /* ------------------------------- asset loading ------------------------------- */

  /** Preload the captain avatars + the stall GLB (idempotent). Call as the ship nears an outpost so the
   *  transition into the port is instant. Falls back to procedural avatars if the loader is unavailable. */
  async preload(): Promise<void> {
    if (this.loadStarted) return;
    this.loadStarted = true;
    try {
      const [{ GLTFLoader }, { MeshoptDecoder }] = await Promise.all([
        import('three/addons/loaders/GLTFLoader.js'),
        import('three/addons/libs/meshopt_decoder.module.js'),
      ]);
      const loader = new GLTFLoader() as unknown as GltfLoader;
      loader.setMeshoptDecoder(MeshoptDecoder);
      const loadTemplate = async (url: string, skinId: number): Promise<Template | null> => {
        try {
          const gltf = await loader.loadAsync(url);
          const model = gltf.scene;
          model.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) { m.castShadow = false; m.receiveShadow = false; m.frustumCulled = false; } });
          if (skinId > 0) this.applySkinMaterialTweaks(model, skinId); // DoubleSide + spectral eyes for skins
          const box = new THREE.Box3().setFromObject(model);
          const size = box.getSize(new THREE.Vector3());
          const scale = AVATAR_HEIGHT / (size.y || 1.8);
          return { scene: model, clips: gltf.animations ?? [], scale };
        } catch (err) { console.warn('[port] avatar load failed', url, err); return null; }
      };
      await Promise.all([
        // the 4 class captains (skin 0 = default per-hull)
        ...Object.entries(CAPTAIN_MODELS).map(async ([clsStr, url]) => {
          const tpl = await loadTemplate(url, 0); if (tpl) this.templates.set(Number(clsStr), tpl);
        }),
        // the cosmetic skins (Onda Avatares) — same 24-bone/6-clip contract, keyed by AVATAR_SKINS id
        ...AVATAR_SKINS.filter((s) => s.id > 0 && s.model).map(async (s) => {
          const tpl = await loadTemplate(s.model, s.id); if (tpl) this.skinTemplates.set(s.id, tpl);
        }),
      ]);
      try {
        const stall = await loader.loadAsync(STALL_MODEL);
        stall.scene.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) { m.castShadow = false; m.receiveShadow = false; } });
        this.stallTemplate = stall.scene;
        this.placeStalls();
      } catch (err) { console.warn('[port] stall load failed', err); }
    } catch (err) {
      console.warn('[port] GLTF loader unavailable — procedural avatars', err);
    }
    this.reskinProceduralAvatars(); // upgrade any capsule avatar spawned before the GLBs landed (first entry)
    // Onda NPC Mercante — build the city's merchant now that the captain templates exist (deferred from entry).
    if (this.pendingMerchantOutpost != null && this.templates.size) this.buildMerchant(this.pendingMerchantOutpost);
    if (this.pendingDealersOutpost != null && this.templates.size) this.buildDealers(this.pendingDealersOutpost);
    this._loaded = true;
  }

  /** Onda 6.5: preload the Capital kit GLBs (idempotent). Call when the ship enters the capital bay so the
   *  ~1s entry fade hides all the loading. Bakes each GLB into one instanceable geometry per kind; when the
   *  kit lands the static world is rebuilt to swap procedural proxies for the instanced city. */
  async preloadCapital(): Promise<void> {
    // Return the SHARED in-flight promise so concurrent callers (editor boot + the Save-Online auto-load's
    // `ready` gate) all await the SAME completion — an early `return` here used to resolve before the kit
    // finished baking, landing a load on a half-built scene (props dropped).
    if (this.kitLoadPromise) return this.kitLoadPromise;
    this.kitLoadPromise = this.doPreloadCapital();
    return this.kitLoadPromise;
  }
  private async doPreloadCapital(): Promise<void> {
    try {
      const [{ GLTFLoader }, { MeshoptDecoder }] = await Promise.all([
        import('three/addons/loaders/GLTFLoader.js'),
        import('three/addons/libs/meshopt_decoder.module.js'),
      ]);
      const loader = new GLTFLoader() as unknown as GltfLoader;
      loader.setMeshoptDecoder(MeshoptDecoder);
      await Promise.all(Object.entries(KIT_URL).map(async ([kind, url]) => {
        try {
          const gltf = await loader.loadAsync(url);
          const baked = this.bakeTemplate(gltf.scene);
          if (baked) {
            // Onda Cidade Verde — Meshy bakes single-sided; force DoubleSide so a tree's foliage / a thin cliff
            // face reads from every angle (same fix the sea-view landmark loader applies to these GLBs).
            if ((LANDSCAPE_KINDS.has(kind) || SKULL_HARBOR_KINDS.has(kind)) && !Array.isArray(baked.mat)) (baked.mat as THREE.MeshStandardMaterial).side = THREE.DoubleSide;
            this.kitBaked.set(kind, baked);
          }
        } catch (err) { console.warn('[capital] kit load failed', kind, url, err); }
      }));
      // Hero-building LOD — load the decimated `.lod.glb` beside each heavy diorama's full mesh so the walkable
      // port can swap it in past ~400 m (a citadel used as a distant backdrop). Baked/DoubleSided identically; the
      // instancer normalizes it with the FULL bake's box so the two are pixel-aligned. A missing/failed LOD just
      // leaves the full mesh always used for that kind.
      await Promise.all([...HEAVY_LOD_KINDS].map(async (kind) => {
        const full = KIT_URL[kind]; if (!full || !this.kitBaked.has(kind)) return;
        try {
          const gltf = await loader.loadAsync(full.replace('.opt.glb', '.lod.glb'));
          const baked = this.bakeTemplate(gltf.scene);
          if (baked) { if (!Array.isArray(baked.mat)) (baked.mat as THREE.MeshStandardMaterial).side = THREE.DoubleSide; this.kitLodBaked.set(kind, baked); }
        } catch (err) { console.warn('[capital] lod load failed', kind, err); }
      }));
      // Onda Cidade Verde — the boulder has no GLB: build it procedurally so it instances/edits exactly like a
      // baked kit prop (the sea-view landmark's rock is procedural for the same reason).
      if (!this.kitBaked.has('rock')) this.kitBaked.set('rock', this.buildProcRock());
      // Onda Cidade Viva — the tavern stool has no GLB either: built procedurally (see buildProcStool) so it
      // instances/edits/sits like any baked kit prop, at zero asset cost.
      if (!this.kitBaked.has('stool')) this.kitBaked.set('stool', this.buildProcStool());
      // Onda Sombras — the shadow decals have no GLB either: bake a flat unlit plane per kind from its soft
      // alpha webp, so instanceKind/buildKitProp place them exactly like any other baked kit prop.
      const texLoader = new THREE.TextureLoader();
      await Promise.all(Object.entries(SHADOW_TEX).map(async ([kind, url]) => {
        if (this.kitBaked.has(kind)) return;
        try { this.kitBaked.set(kind, this.makeShadowBaked(await texLoader.loadAsync(url))); }
        catch (err) { console.warn('[capital] shadow decal load failed', kind, url, err); }
      }));
      this.kitReady = this.kitBaked.size > 0;
      // rebuild the static world to swap proxies for instanced GLBs — for the capital OR a kit village (props).
      if ((this.isCapital || (this.layout.props?.length ?? 0) > 0) && this.kitReady) { this.disposeStatic(); this.buildStaticWorld(); }
    } catch (err) {
      console.warn('[capital] kit loader unavailable — procedural city', err);
    }
  }

  /** Bake a loaded kit GLB into a single instanceable geometry (template space) + its material + bbox.
   *  Keeps only position/normal/uv so multi-mesh GLBs merge cleanly; single-mesh GLBs skip the merge. */
  private bakeTemplate(obj: THREE.Object3D): KitBaked | null {
    obj.position.set(0, 0, 0); obj.rotation.set(0, 0, 0); obj.scale.set(1, 1, 1);
    obj.updateMatrixWorld(true);
    const geos: THREE.BufferGeometry[] = [];
    let mat: THREE.Material | null = null;
    obj.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh || !m.geometry) return;
      const g = m.geometry.clone();
      for (const name of Object.keys(g.attributes)) if (name !== 'position' && name !== 'normal' && name !== 'uv') g.deleteAttribute(name);
      if (!g.attributes.normal) g.computeVertexNormals();
      if (!g.attributes.uv) { const n = g.attributes.position!.count; g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(n * 2), 2)); }
      g.applyMatrix4(m.matrixWorld);
      geos.push(g);
      if (!mat) mat = Array.isArray(m.material) ? m.material[0]! : m.material;
    });
    if (!geos.length || !mat) return null;
    let merged: THREE.BufferGeometry | null = geos.length === 1 ? geos[0]! : mergeGeometries(geos, false);
    if (!merged) merged = geos[0]!; // merge failed (mismatched index) → just use the largest piece
    const box = new THREE.Box3().setFromBufferAttribute(merged.attributes.position as THREE.BufferAttribute);
    return { geo: merged, mat, box };
  }

  /** Onda Cidade Verde — the procedural coastal boulder (no GLB). A fat rounded rock + two smaller ones nestled
   *  at its base, faceted grey stone (vertex-coloured), base rested at y=0 so instanceKind/buildKitProp treat it
   *  exactly like a baked kit prop. Native ~4 m across → KIT_FOOT('rock') renormalizes it to the city size. */
  private buildProcRock(): KitBaked {
    const col = new THREE.Color();
    const parts: THREE.BufferGeometry[] = [];
    const blob = (r: number, x: number, z: number, y: number, hex: number): void => {
      const s = new THREE.IcosahedronGeometry(r, 0); s.scale(1, 0.72, 1); s.translate(x, y, z);
      const n = s.attributes.position!.count; col.set(hex);
      const c = new Float32Array(n * 3);
      for (let i = 0; i < n; i++) { c[i * 3] = col.r; c[i * 3 + 1] = col.g; c[i * 3 + 2] = col.b; }
      s.setAttribute('color', new THREE.BufferAttribute(c, 3));
      for (const nm of Object.keys(s.attributes)) if (nm !== 'position' && nm !== 'normal' && nm !== 'color') s.deleteAttribute(nm);
      parts.push(s);
    };
    blob(1.4, 0, 0, 0.9, 0x8a8f96); blob(0.9, 1.3, 0.25, 0.5, 0x7d838b); blob(0.7, -1.15, -0.45, 0.45, 0x969ba1);
    let geo = parts.length === 1 ? parts[0]! : (mergeGeometries(parts, false) ?? parts[0]!);
    geo.computeBoundingBox();
    geo.translate(0, -geo.boundingBox!.min.y, 0); // rest the base at y=0
    const box = new THREE.Box3().setFromBufferAttribute(geo.attributes.position as THREE.BufferAttribute);
    return { geo, mat: new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 1, side: THREE.DoubleSide }), box };
  }

  /** Onda Cidade Viva — the procedural tavern stool (no GLB): a turned wooden seat (lathe-chamfered rim, lightly
   *  dished top) on four gently splayed legs braced by a square stretcher ring. Dark wood in two tones (oak seat +
   *  walnut legs) with per-vertex brightness jitter — no texture. The seat disc IS the widest part (0.35 m across),
   *  so KIT_FOOT('stool')=0.35 renders it at native size with its seat surface at ~0.45 m (= CITY_SEAT.stool.y0).
   *  Base rested at y=0 so instanceKind/buildKitProp/sit treat it exactly like a baked kit prop. */
  private buildProcStool(): KitBaked {
    const col = new THREE.Color();
    const rnd = mulberry32(0x57001);                        // deterministic per-vertex tone jitter
    const UPv = new THREE.Vector3(0, 1, 0);
    const parts: THREE.BufferGeometry[] = [];
    // paint a part one wood tone with a small per-vertex brightness wobble, then trim to pos/normal/color so all
    // parts merge cleanly (mirrors buildProcRock's blob()).
    const paint = (g: THREE.BufferGeometry, hex: number, jit = 0.07): void => {
      const n = g.attributes.position!.count; col.set(hex);
      const c = new Float32Array(n * 3);
      for (let i = 0; i < n; i++) { const f = 1 + (rnd() * 2 - 1) * jit; c[i * 3] = col.r * f; c[i * 3 + 1] = col.g * f; c[i * 3 + 2] = col.b * f; }
      g.setAttribute('color', new THREE.BufferAttribute(c, 3));
      for (const nm of Object.keys(g.attributes)) if (nm !== 'position' && nm !== 'normal' && nm !== 'color') g.deleteAttribute(nm);
      parts.push(g);
    };
    // a strut (leg / stretcher) as a tapered cylinder from a→b, oriented + placed by a compose matrix.
    const strut = (a: THREE.Vector3, b: THREE.Vector3, r0: number, r1: number, seg: number, hex: number): void => {
      const dir = b.clone().sub(a); const len = dir.length() || 1e-4; dir.normalize();
      const g = new THREE.CylinderGeometry(r0, r1, len, seg);
      const q = new THREE.Quaternion().setFromUnitVectors(UPv, dir);
      const mid = a.clone().add(b).multiplyScalar(0.5);
      g.applyMatrix4(new THREE.Matrix4().compose(mid, q, new THREE.Vector3(1, 1, 1)));
      paint(g, hex);
    };
    // turned seat disc — chamfered rim (top radius < bottom) with the top-cap centre dished down a touch so the
    // saddle reads lightly concave. Top surface at y=0.45 (the seat height).
    const seat = new THREE.CylinderGeometry(0.155, 0.175, 0.055, 16); seat.translate(0, 0.4225, 0);
    const sp = seat.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < sp.count; i++) if (sp.getY(i) > 0.448 && Math.hypot(sp.getX(i), sp.getZ(i)) < 0.02) sp.setY(i, sp.getY(i) - 0.015);
    seat.computeVertexNormals();
    paint(seat, 0x5a3d26);
    // four splayed legs + the square stretcher ring bracing them, tucked just inside the seat rim (so the seat sets
    // the footprint). Nodes at y=0.16 sit on the legs (r interpolates 0.15→0.09 over the 0.40 m rise → 0.126).
    const nodes: THREE.Vector3[] = [];
    for (let i = 0; i < 4; i++) {
      const ang = Math.PI / 4 + i * Math.PI / 2, cx = Math.cos(ang), cz = Math.sin(ang);
      strut(new THREE.Vector3(0.15 * cx, 0, 0.15 * cz), new THREE.Vector3(0.09 * cx, 0.40, 0.09 * cz), 0.024, 0.016, 6, 0x462d18);
      nodes.push(new THREE.Vector3(0.126 * cx, 0.16, 0.126 * cz));
    }
    for (let i = 0; i < 4; i++) strut(nodes[i]!, nodes[(i + 1) % 4]!, 0.012, 0.012, 5, 0x5a3d26);
    let geo = parts.length === 1 ? parts[0]! : (mergeGeometries(parts, false) ?? parts[0]!);
    geo.computeBoundingBox();
    geo.translate(0, -geo.boundingBox!.min.y, 0); // rest the base at y=0
    const box = new THREE.Box3().setFromBufferAttribute(geo.attributes.position as THREE.BufferAttribute);
    return { geo, mat: new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.82, metalness: 0 }), box };
  }

  /** Onda Sombras — bake a ground-shadow decal into a KitBaked: a unit plane lying FLAT on XZ (base at y=0, same
   *  convention as buildGroundMesh) textured with the soft alpha mask. Unlit (MeshBasic ignores lights → the
   *  decal never brightens at midday nor darkens at night), transparent with depthWrite OFF so it never occludes
   *  the prop above it, polygonOffset + a high renderOrder (set on the InstancedMesh in instanceKind) so it draws
   *  cleanly over the plaza floor with no z-fighting. Footprint-scaled by KIT_FOOT like any kit prop. */
  private makeShadowBaked(tex: THREE.Texture): KitBaked {
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 4;
    const geo = new THREE.PlaneGeometry(1, 1);
    geo.rotateX(-Math.PI / 2); // lie flat, normal +Y (same as the ground patches)
    const mat = new THREE.MeshBasicMaterial({
      map: tex, transparent: true, depthWrite: false, side: THREE.DoubleSide,
      polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
    });
    const box = new THREE.Box3().setFromBufferAttribute(geo.attributes.position as THREE.BufferAttribute);
    return { geo, mat, box };
  }

  /* ------------------------------- static plaza ------------------------------- */

  protected buildStaticWorld(): void {
    this.torchPlaces.length = 0; this.torchGroup = null; // fresh torch set per (re)build
    if (this.isCapital) this.buildCapitalWorld();
    else this.buildVillageWorld();
    this.applyCityTime(); // paint the correct time-of-day into the fresh scene so entry never flashes daylight
  }

  /** Onda Cidade Noturna — retint the scene's GLOBAL lights + sky/fog + window glow + torch lights for the
   *  current time of day (the SAME wall-clock cycle the sea sky uses via cityPhase). Allocation-free. Returns
   *  the torch-flame boost the caller folds into the flicker. A no-op (returns 1) in the editor or before the
   *  lights are built (e.g. the house shell), so the City Layout Editor keeps neutral daylight and the warm
   *  cabin keeps its own interior lighting. Adds ZERO lights — it only recolours the existing three. */
  private applyCityTime(): number {
    if (this.editorMode || !this.hemiLight || !this.sunLight || !this.ambLight) return 1;
    const phase = this.phaseOverride != null ? this.phaseOverride : cityPhase();
    const pal = cityPaletteAt(phase, this._cityPal);
    (this.scene.background as THREE.Color).setHex(pal.sky);
    const fog = this.scene.fog as THREE.Fog | null;
    if (fog) { fog.color.setHex(pal.fog); fog.near = this.baseFogNear * (1 - 0.28 * pal.nightFactor); fog.far = this.baseFogFar; }
    this.hemiLight.color.setHex(pal.hemiSky);
    this.hemiLight.groundColor.setHex(pal.hemiGround);
    this.hemiLight.intensity = pal.hemiI;
    this.sunLight.color.setHex(pal.sun);
    this.sunLight.intensity = pal.sunI;
    // gentle key-light azimuth swing — kept HIGH overhead (y≫horizontal) so night is a soft moon-key, never underlit
    const az = phase * Math.PI * 2;
    this.sunLight.position.set(Math.sin(az) * 55, 78, Math.cos(az) * 55);
    this.ambLight.color.setHex(pal.ambient);
    this.ambLight.intensity = pal.ambientI;
    const winI = pal.windowGlow * 1.6; // lit windows ramp dark → warm
    for (const m of this.windowMats) m.emissiveIntensity = winI;
    for (const l of this.torchLights) l.intensity = (l.userData.baseI as number) * pal.torchBoost;
    return pal.torchBoost;
  }

  /** Onda Cidade Noturna — pin the city time-of-day phase 0..1 (render-only; dev/E2E hook exposed on __cc as
   *  setCityPhase), or null to resume the live cycle. Ignored while editing (the editor stays neutral). */
  setPhaseOverride(p: number | null): void { this.phaseOverride = p; }

  private buildVillageWorld(): void {
    const L = this.layout;
    // Onda Skull Harbor — a village whose layout carries a KIT prop (the redesigned VILLAGE1's tavern_skull/inn/…)
    // renders those props the SAME instanced/windowed/lamped way the capital does. Onda de-proc — the shipped plaza
    // now carries its well/crates/tavern as LEGACY props, so "has a real kit" means "has a prop that is NOT a legacy
    // procedural kind": the untouched plaza (all-legacy props) keeps hasKit=false → every gate below stays on its
    // shipped branch (the centre ring is kept), so its scene is byte-identical to before.
    const props: readonly PortProp[] = L.props ?? [];
    const hasKit = !this.editorMode && props.some((p) => !LEGACY_PROC_KINDS.has(p.kind));
    this.scene.background = new THREE.Color(0x9ec7e6);
    this.scene.fog = new THREE.Fog(0xbcd6e8, 60, 260);
    this.baseFogNear = 60; this.baseFogFar = 260;

    // lights — warm key + cool sky fill; the port reads golden-hour so torches pop. Kept as refs so the
    // day/night cycle (applyCityTime) can retint them each frame — noon reproduces this shipped look.
    this.hemiLight = new THREE.HemisphereLight(0xcfe6ff, 0x5a4632, 1.0); this.staticRoot.add(this.hemiLight);
    const sun = new THREE.DirectionalLight(0xffe9c2, 1.35);
    sun.position.set(-30, 60, 20);
    this.staticRoot.add(sun); this.sunLight = sun;
    this.ambLight = new THREE.AmbientLight(0x6b5a44, 0.35); this.staticRoot.add(this.ambLight);

    // the open sea the dock overlooks (a big flat teal plane just under the plaza)
    const sea = new THREE.Mesh(
      new THREE.PlaneGeometry(1200, 1200),
      new THREE.MeshStandardMaterial({ color: 0x2c6d84, roughness: 0.85, metalness: 0.05 }),
    );
    sea.rotation.x = -Math.PI / 2;
    sea.position.set(L.size / 2, -0.35, L.size / 2 - 60);
    this.staticRoot.add(sea);

    // plaza cobblestone floor (real tiled texture)
    const floor = new THREE.Mesh(
      new THREE.BoxGeometry(L.size, 0.4, L.size),
      new THREE.MeshStandardMaterial({ map: this.tiledTex('/textures/cobblestone.webp', L.size / 8, L.size / 8), roughness: 0.95 }),
    );
    floor.position.set(L.size / 2, -0.2, L.size / 2);
    this.staticRoot.add(floor);
    // a warm inlaid ring pattern in the plaza centre (a splash of colour). A kit village owns its own centre-
    // piece (VILLAGE1's treasure tree), so the ring is skipped there — it never sat under a kit prop before.
    if (!hasKit) {
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(4.5, 6.2, 40),
        new THREE.MeshStandardMaterial({ color: 0x8a6a3a, roughness: 1, side: THREE.DoubleSide }),
      );
      ring.rotation.x = -Math.PI / 2; ring.position.set(L.size / 2, 0.02, L.size / 2);
      this.staticRoot.add(ring);
    }
    if (!this.editorMode) this.buildGround(); // render-only editable floor patches (none by default in a village)

    // perimeter low wall (except the dock gap) — sells the invisible bounds
    const wallMat = new THREE.MeshStandardMaterial({ color: 0x7d6a4c, roughness: 1 });
    const wall = (x: number, z: number, w: number, d: number) => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(w, 1.1, d), wallMat);
      m.position.set(x, 0.35, z); this.staticRoot.add(m);
    };
    wall(L.size / 2, L.size - 0.5, L.size, 1);           // north
    wall(0.5, L.size / 2, 1, L.size);                    // west
    wall(L.size - 0.5, L.size / 2, 1, L.size);           // east
    wall(11, 0.5, 22, 1); wall(L.size - 11, 0.5, 22, 1); // south, split around the dock gap (x 24..40)

    // the exit dock — real weathered planks running out over the water (south)
    const dockMat = new THREE.MeshStandardMaterial({ map: this.tiledTex('/textures/dock_planks.webp', 1.8, 2.4), roughness: 1 });
    const plankMat = new THREE.MeshStandardMaterial({ color: 0x5a3d24, roughness: 1 });
    const dock = new THREE.Mesh(new THREE.BoxGeometry(16, 0.3, 22), dockMat);
    dock.position.set(32, -0.05, -4);
    this.staticRoot.add(dock);
    for (let i = 0; i < 4; i++) { // mooring posts
      const post = new THREE.Mesh(new THREE.CylinderGeometry(0.35, 0.4, 2.4, 8), plankMat);
      post.position.set(24.5 + (i % 2) * 15, 0.6, -12 + Math.floor(i / 2) * 8);
      this.staticRoot.add(post);
    }
    this.addTorch(24.5, -12, 0.6); this.addTorch(39.5, -12, 0.6); // dock lanterns

    // Onda de-proc — the central well + crate stacks + the procedural box tavern are now DATA (LEGACY_PROC_KINDS
    // props on the layout), rendered by a dedicated builder with geometry IDENTICAL to the old baked block so the
    // shipped plaza is byte-identical, yet the owner can move/delete them in the village editor. A KIT village
    // (VILLAGE1) carries no legacy props, so this loop is a no-op there (its treasure tree / tavern_skull replace
    // the dressing). Runs in the live scene only — the editor owns its own selectable prop layer.
    if (!this.editorMode) this.buildLegacyProps(props);
    // fallback procedural stalls until the GLB arrives (placeStalls swaps them in). In the Layout Editor the
    // editor owns the stall props, so skip the procedural set.
    if (!this.editorMode) for (const s of L.stalls) this.buildProceduralStall(s.x, s.y, s.heading);
    this.finalizeTorches();
    // Onda Skull Harbor — instance the kit props exactly like the capital (GLBs + night windows + lamps). Kit not
    // baked yet (villages preload on entry, see main.ts) → cheap proxies now, swapped by doPreloadCapital's rebuild
    // the instant the GLBs land. placeCapitalKit iterates L.props, so a propless village never enters this branch.
    if (hasKit) { if (this.kitReady) this.placeCapitalKit(); else this.buildCapitalFallback(); }
  }

  /* ------------------------------- the Capital (Onda 6.5) ------------------------------- */

  /** Build the dense Capital city: floor + main street + dock, then the modular kit. If the kit GLBs are
   *  loaded it instances them; otherwise it drops cheap procedural proxies that placeCapitalKit() swaps out
   *  the instant the GLBs land (they're preloaded on bay approach, so the fade hides all of it). */
  private buildCapitalWorld(): void {
    const L = this.layout, S = L.size;
    this.scene.background = new THREE.Color(0x9cc3e4);
    this.scene.fog = new THREE.Fog(0xbcd6e8, 90, 460);
    this.baseFogNear = 90; this.baseFogFar = 460;

    // warm golden-hour light for the harbour city. Kept as refs so applyCityTime can drive the full cycle —
    // noon reproduces this exact shipped look; night swaps to a cool moonlit key + blue ambient floor.
    this.hemiLight = new THREE.HemisphereLight(0xdfeeff, 0x4a3a28, 1.0); this.staticRoot.add(this.hemiLight);
    const sun = new THREE.DirectionalLight(0xffe6bc, 1.5);
    sun.position.set(-40, 80, 30); this.staticRoot.add(sun); this.sunLight = sun;
    this.ambLight = new THREE.AmbientLight(0x6b5a44, 0.32); this.staticRoot.add(this.ambLight);

    // open sea beyond the seawall (south, toward the dock/harbour mouth)
    const sea = new THREE.Mesh(new THREE.PlaneGeometry(2400, 2400), new THREE.MeshStandardMaterial({ color: 0x2c6d84, roughness: 0.85, metalness: 0.05 }));
    sea.rotation.x = -Math.PI / 2; sea.position.set(S / 2, -0.4, S / 2 - 120); this.staticRoot.add(sea);

    // cobbled base ground (real tiled texture). The lighter main-street flagstone + the old centre ring are
    // now DATA (layout.ground patches, drawn by buildGround) so the owner can reshape/remove them in the editor.
    const floor = new THREE.Mesh(new THREE.BoxGeometry(S, 0.4, S), new THREE.MeshStandardMaterial({ map: this.tiledTex('/textures/cobblestone.webp', S / 8, S / 8), roughness: 0.98 }));
    floor.position.set(S / 2, -0.2, S / 2); this.staticRoot.add(floor);
    if (!this.editorMode) this.buildGround(); // editor owns a selectable ground layer instead

    // the entry dock — real weathered planks out over the water at the south harbour gate
    const dockMat = new THREE.MeshStandardMaterial({ map: this.tiledTex('/textures/dock_planks.webp', 2, 2.2), roughness: 1 });
    const plankMat = new THREE.MeshStandardMaterial({ color: 0x5a3d24, roughness: 1 });
    const dock = new THREE.Mesh(new THREE.BoxGeometry(18, 0.3, 20), dockMat);
    dock.position.set(48, -0.05, -3); this.staticRoot.add(dock);
    for (let i = 0; i < 4; i++) { const post = new THREE.Mesh(new THREE.CylinderGeometry(0.4, 0.45, 2.6, 8), plankMat); post.position.set(40 + (i % 2) * 16, 0.7, -11 + Math.floor(i / 2) * 8); this.staticRoot.add(post); }

    // a handful of torches only (draw-call budget): dock, gate, plaza, tavern porch
    this.addTorch(40, -11, 0.7); this.addTorch(56, -11, 0.7);
    this.addTorch(42, 17, 0.2);  this.addTorch(54, 17, 0.2);
    this.addTorch(40, 60, 0.2);  this.addTorch(56, 60, 0.2);

    // the modular kit — instanced GLBs when ready, else cheap proxies swapped by placeCapitalKit()
    if (this.editorMode) { /* Creative-Mode editor owns the prop layer — instance nothing here */ }
    else if (this.kitReady) this.placeCapitalKit();
    else this.buildCapitalFallback();
    this.finalizeTorches();
  }

  /** Cheap procedural stand-ins for the kit while the GLBs stream in (rarely seen — kit preloads on the
   *  bay approach). One box per solid prop + a couple of long perimeter walls. */
  private buildCapitalFallback(): void {
    const wallMat = new THREE.MeshStandardMaterial({ color: 0x8a7a5c, roughness: 1 });
    const stoneMat = new THREE.MeshStandardMaterial({ color: 0x9a8a6c, roughness: 1 });
    const g = new THREE.Group(); g.userData.capFallback = true;
    const box = (x: number, y: number, w: number, h: number, d: number, mat: THREE.Material): void => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat); m.position.set(x, h / 2, y); g.add(m);
    };
    const S = this.layout.size;
    // perimeter walls (long boxes) — capital only: a kit village already builds its own low walls in
    // buildVillageWorld, and these use capital-sized coords (x=76) that fall outside a 64 m village plaza.
    if (this.isCapital) {
      box(S / 2, S - 2, S - 4, 3.2, 2, wallMat); box(2, S / 2, 2, 3.2, S - 4, wallMat); box(S - 2, S / 2, 2, 3.2, S - 4, wallMat);
      box(20, 2, 30, 3.2, 2, wallMat); box(76, 2, 30, 3.2, 2, wallMat);
    }
    for (const p of this.layout.props ?? []) {
      if (p.kind === 'wall') continue;
      const tall = p.kind === 'lighthouse' ? 14 : p.kind === 'gate' ? 8 : p.kind === 'tavern' ? 7 : 5;
      const w = p.kind === 'tavern' ? 12 : p.kind === 'gate' ? 12 : 6;
      box(p.x, p.y, w, tall, w, stoneMat);
    }
    this.staticRoot.add(g);
  }

  /** Instance every kit kind DENSE from the loaded GLBs (one InstancedMesh per kind → ~1 draw call each). */
  private placeCapitalKit(): void {
    if (!this.kitReady) return;
    // drop the procedural fallback if it's up
    for (const c of [...this.staticRoot.children]) if (c.userData.capFallback) { this.staticRoot.remove(c); c.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) { m.geometry?.dispose?.(); (m.material as THREE.Material)?.dispose?.(); } }); }
    const byKind = new Map<string, { x: number; y: number; heading: number; h?: number; s?: number }[]>();
    for (const p of this.layout.props ?? []) { (byKind.get(p.kind) ?? byKind.set(p.kind, []).get(p.kind)!).push(p); }
    const missing: string[] = []; // bug #1: kit GLBs that failed to bake this session — never dropped silently
    for (const [kind, places] of byKind) {
      // legacy procedural dressing (well/crate/old tavern) is drawn by buildLegacyProps — never a baked kit kind,
      // so skip it here (else it would proxy-box below).
      if (LEGACY_PROC_KINDS.has(kind)) continue;
      // NOT baked → the prop would VANISH silently (the "alguns itens não carregaram" village bug). Draw proxy
      // boxes so it stays visible, and (for a real GLB kind) queue a one-shot retry that swaps in the true mesh.
      if (!this.kitBaked.has(kind)) { this.buildProxyProps(kind, places); if (KIT_URL[kind]) missing.push(kind); continue; }
      // Hero-building LOD — a heavy diorama gets a full+lod pair swapped by camera distance (updatePortLod); every
      // other kind is one InstancedMesh. If the LOD isn't loaded the pair falls back to a plain full mesh.
      if (HEAVY_LOD_KINDS.has(kind) && this.kitLodBaked.has(kind)) {
        const full = this.makeKitInstances(kind, places, false);
        const lod = this.makeKitInstances(kind, places, true);
        if (full && lod) {
          this.staticRoot.add(full); lod.visible = false; this.staticRoot.add(lod);
          let cx = 0, cz = 0; for (const p of places) { cx += p.x; cz += p.y; } cx /= places.length; cz /= places.length;
          this.portLodPairs.push({ full, lod, cx, cz, far: false });
          continue;
        }
        if (full) this.staticRoot.add(full);
        continue;
      }
      this.instanceKind(kind, places);
    }
    this.updatePortLod(); // set each pair's initial full/lod visibility for the current camera
    this.buildCityWindows();
    this.buildCityLamps();
    if (missing.length) this.retryKitBakes(missing);
  }

  /** Bug #1 hardening — draw cheap proxy boxes for a kit `kind` whose GLB hasn't baked (a failed/pending load),
   *  so a placed prop is NEVER invisible. Sized to the kind's KIT_FOOT/KIT_TALL so the stand-in reads at roughly
   *  the real prop's scale; disposed + swapped for the real mesh when the bake lands (buildStaticWorld rebuild). */
  private buildProxyProps(kind: string, places: { x: number; y: number; heading: number; h?: number; s?: number }[]): void {
    if (!places.length) return;
    const foot = KIT_FOOT[kind] ?? 6, tall = KIT_TALL[kind] ?? foot;
    const mat = new THREE.MeshStandardMaterial({ color: 0x9a8a6c, roughness: 1 });
    const g = new THREE.Group();
    for (const p of places) {
      const s = p.s ?? 1;
      const box = new THREE.Mesh(new THREE.BoxGeometry(foot * s, tall * s, foot * s), mat);
      box.position.set(p.x, (tall * s) / 2 + (p.h ?? 0), p.y); box.rotation.y = -p.heading;
      g.add(box);
    }
    this.staticRoot.add(g);
  }

  /** Bug #1 hardening — a kit GLB that failed to bake used to make its props vanish for the whole session (the
   *  scene bakes ONCE). Retry the failed kinds' loads ONE more time; on any success, rebuild so the proxy boxes
   *  swap for the real meshes. Never silent: warns exactly which kinds are showing as proxies. Bounded (each kind
   *  retried at most once) so a genuinely-404 asset can't loop. */
  private retryKitBakes(kinds: string[]): void {
    const todo = kinds.filter((k) => KIT_URL[k] && !this.kitBaked.has(k) && !this.kitRetried.has(k));
    if (!todo.length) return;
    for (const k of todo) this.kitRetried.add(k);
    // eslint-disable-next-line no-console
    console.warn('[port] kit GLB(s) failed to bake — showing proxy boxes, retrying once:', todo.join(', '));
    void (async () => {
      try {
        const [{ GLTFLoader }, { MeshoptDecoder }] = await Promise.all([
          import('three/addons/loaders/GLTFLoader.js'),
          import('three/addons/libs/meshopt_decoder.module.js'),
        ]);
        const loader = new GLTFLoader() as unknown as GltfLoader;
        loader.setMeshoptDecoder(MeshoptDecoder);
        let any = false;
        await Promise.all(todo.map(async (kind) => {
          try {
            const baked = this.bakeTemplate((await loader.loadAsync(KIT_URL[kind]!)).scene);
            if (baked) {
              if ((LANDSCAPE_KINDS.has(kind) || SKULL_HARBOR_KINDS.has(kind)) && !Array.isArray(baked.mat)) (baked.mat as THREE.MeshStandardMaterial).side = THREE.DoubleSide;
              this.kitBaked.set(kind, baked); this.kitReady = true; any = true;
            }
          } catch (err) { console.warn('[port] kit retry failed', kind, err); }
        }));
        if (any && (this.isCapital || (this.layout.props?.length ?? 0) > 0)) { this.disposeStatic(); this.buildStaticWorld(); }
      } catch (err) { console.warn('[port] kit retry loader unavailable', err); }
    })();
  }

  /** Hero-building LOD — swap each heavy diorama's full/lod pair by the follow camera's distance to the kind's
   *  placement centroid (30 m hysteresis, mirroring the ship + sea-kit LOD). Cheap (a handful of pairs); called
   *  each frame from sync. In the walkable plaza the camera is usually near, so the full mesh stays up — the LOD
   *  only kicks in for a prop the owner planted as a far backdrop. */
  private updatePortLod(): void {
    if (!this.portLodPairs.length) return;
    const cx = this.camera.position.x, cz = this.camera.position.z;
    for (const e of this.portLodPairs) {
      const dx = e.cx - cx, dz = e.cz - cz, d2 = dx * dx + dz * dz;
      // LIGHT MODE keeps the decimated LOD up at ALL distances — a wallet webview can't spare the full mesh's tris.
      e.far = this.lowMem ? true : (e.far ? d2 > PORT_LOD_NEAR_D2 : d2 > PORT_LOD_FAR_D2); // hysteresis: full only within NEAR
      e.full.visible = !e.far;
      e.lod.visible = e.far;
    }
  }

  /** Onda Cidade Viva — ONE instanced emissive glow head per lamp kind (street lamp + lantern grove) at each
   *  placement, so the owner's lamps "acendem de noite" exactly like the house/city windows. The kit GLBs bake
   *  into a single opaque material per kind (bakeTemplate keeps only material[0]), so — per the same art brief as
   *  buildCityWindows — a small dark-by-day / warm-by-night sphere is dropped at the flame head instead of tinting
   *  the whole iron post. Registered in windowMats so applyCityTime ramps it with pal.windowGlow (no extra light).
   *  ≤ +1 draw call per lamp kind; skipped in the editor (owner edits in neutral daylight). */
  private buildCityLamps(): void {
    if (this.cityLampsGroup) { this.staticRoot.remove(this.cityLampsGroup); this.cityLampsGroup = null; }
    if (this.editorMode) return;
    const byKind = new Map<string, { x: number; y: number; h?: number }[]>();
    for (const p of this.layout.props ?? []) { if (p.kind in LAMP_KINDS) (byKind.get(p.kind) ?? byKind.set(p.kind, []).get(p.kind)!).push(p); }
    if (!byKind.size) return;
    const grp = new THREE.Group(); grp.userData.cityLamps = true;
    const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), one = new THREE.Vector3(1, 1, 1), pos = new THREE.Vector3();
    for (const [kind, places] of byKind) {
      const cfg = LAMP_KINDS[kind]!;
      const geo = new THREE.SphereGeometry(cfg.size, 8, 8);
      // dark ember by day (unlit glass) → warm emissive at night, driven by applyCityTime via windowMats.
      const mat = new THREE.MeshStandardMaterial({ color: 0x1a1206, emissive: cfg.color, emissiveIntensity: 0, roughness: 0.6 });
      const mesh = new THREE.InstancedMesh(geo, mat, places.length);
      mesh.frustumCulled = false;
      places.forEach((p, i) => { pos.set(p.x, cfg.headY + (p.h ?? 0), p.y); m4.compose(pos, q, one); mesh.setMatrixAt(i, m4); }); // lift the flame head with the post
      mesh.instanceMatrix.needsUpdate = true;
      grp.add(mesh);
      this.windowMats.push(mat);
    }
    this.staticRoot.add(grp);
    this.cityLampsGroup = grp;
  }

  /** Onda Cidade Noturna — ONE instanced mesh of warm "lit window" quads across the capital's house facades
   *  (+ the tavern), so the walled city glows from within at night. The kit GLBs bake into a single material
   *  per kind with NO separable window mesh, so per the art brief we drop cheap emissive PLANES on the street-
   *  facing wall instead of tinting the whole building. Dark by day, driven warm at night by applyCityTime via
   *  windowMats. ~1 draw call for every window in the city; skipped in the editor (owner edits in neutral light). */
  private buildCityWindows(): void {
    if (this.cityWindowsGroup) { this.staticRoot.remove(this.cityWindowsGroup); this.cityWindowsGroup = null; }
    if (this.editorMode) return;
    // PER-PROP, heading-relative: every house/tavern/cottage in the layout (shipped, owner-placed, rotated, or
    // moved outside the plaza) gets its window/lantern quads. Each spec is authored in the prop's local frame and
    // rotated by the prop's `-heading` yaw (the same yaw instanceKind uses) so the pane lands on the real facade.
    const wins: { x: number; y: number; z: number; ry: number; w: number; h: number }[] = [];
    for (const p of this.layout.props ?? []) {
      const specs = WINDOW_SPECS[p.kind]; if (!specs) continue;
      const a = -p.heading, ca = Math.cos(a), sa = Math.sin(a);
      const lift = p.h ?? 0; // a dwelling raised onto a cliff carries its windows up with it
      for (const s of specs) {
        const wx = p.x + s.dx * ca + s.dz * sa;   // three.js Y-rotation of the local (dx,dz) offset
        const wz = p.y - s.dx * sa + s.dz * ca;
        wins.push({ x: wx, y: s.up + lift, z: wz, ry: s.ry + a, w: s.w, h: s.h });
      }
    }
    if (!wins.length) return;
    const geo = new THREE.PlaneGeometry(1, 1); // unit pane — per-instance scale carries each spec's w×h
    // dark frame + warm emissive: at windowGlow 0 (day) it reads as an unlit recessed pane, not a bright poster.
    const mat = new THREE.MeshStandardMaterial({ color: 0x271c0e, emissive: 0xffcf7a, emissiveIntensity: 0, roughness: 0.7 });
    const mesh = new THREE.InstancedMesh(geo, mat, wins.length);
    mesh.frustumCulled = false;
    const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), s = new THREE.Vector3(), pos = new THREE.Vector3();
    wins.forEach((w, i) => { q.setFromAxisAngle(UP, w.ry); pos.set(w.x, w.y, w.z); s.set(w.w, w.h, 1); m4.compose(pos, q, s); mesh.setMatrixAt(i, m4); });
    mesh.instanceMatrix.needsUpdate = true;
    const grp = new THREE.Group(); grp.userData.cityWindows = true; grp.add(mesh);
    this.staticRoot.add(grp);
    this.cityWindowsGroup = grp;
    this.windowMats.push(mat);
  }

  /** One InstancedMesh for `kind` across all placements, using the baked (template-space) geometry. Each
   *  instance may carry an optional altitude `h` (m above the plaza floor, world-Y — the landscape "rock on a
   *  cliff" case) and an optional uniform render scale `s` (multiplies the kind's base normalization — the owner's
   *  per-prop size knob; absent = 1). The base rests on the floor at every scale (`-minY·k`), then + `h` + the
   *  shadow lift. Render-only: collision/interact read x,y only and never `h`/`s`. */
  private instanceKind(kind: string, places: { x: number; y: number; heading: number; h?: number; s?: number }[]): void {
    const inst = this.makeKitInstances(kind, places);
    if (inst) this.staticRoot.add(inst);
  }

  /** Build (but DON'T attach) ONE InstancedMesh for `kind` across `places`, using the baked template geometry and
   *  the EXACT same grounding/scale/altitude math production uses — so a prop looks identical whether it's drawn
   *  by the live city or by the Creative-Mode editor's instanced prop layer (which calls this to collapse hundreds
   *  of per-prop draw calls down to ~one per kind). Returns null if the kind isn't baked or `places` is empty. */
  makeKitInstances(kind: string, places: { x: number; y: number; heading: number; h?: number; s?: number }[], lod = false): THREE.InstancedMesh | null {
    const baked = this.kitBaked.get(kind);
    if (!baked || places.length === 0) return null;
    // Hero-building LOD — when `lod` is requested, use the decimated geometry/material but keep the FULL bake's box
    // for normalization (so the two meshes sit pixel-aligned). No LOD for this kind → no separate LOD mesh (null).
    const lodBaked = lod ? this.kitLodBaked.get(kind) : undefined;
    if (lod && !lodBaked) return null;
    const src = lodBaked ?? baked;
    const sz = baked.box.getSize(new THREE.Vector3());
    const tall = KIT_TALL[kind];
    const base = tall ? tall / (sz.y || 1) : (KIT_FOOT[kind] ?? 6) / (Math.max(sz.x, sz.z) || 1);
    const minY = baked.box.min.y;
    const isShadow = SHADOW_KINDS.has(kind);
    const lift = isShadow ? SHADOW_LIFT : 0;
    const inst = new THREE.InstancedMesh(src.geo, src.mat, places.length);
    inst.frustumCulled = false;
    inst.userData.keepGeo = true; // shares the cached bake — never disposed on layout switch
    if (isShadow) inst.renderOrder = 2; // transparent decal draws after the opaque floor
    const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), s = new THREE.Vector3(), pos = new THREE.Vector3();
    places.forEach((p, i) => {
      const k = base * (p.s ?? 1); // per-prop uniform size, still grounded (base kept at floor at any scale)
      q.setFromAxisAngle(UP, -p.heading); s.set(k, k, k);
      pos.set(p.x, -minY * k + (p.h ?? 0) + lift, p.y);
      m4.compose(pos, q, s); inst.setMatrixAt(i, m4);
    });
    inst.instanceMatrix.needsUpdate = true;
    return inst;
  }

  /* ---------------------- Creative-Mode Layout Editor hooks (owner tool, client-only) ---------------------- */

  /** Toggle the standalone Layout Editor mode and rebuild: the scene keeps its environment (floor/sea/dock/
   *  lights/torches) but instances NO kit props — the editor renders an individually-selectable prop layer.
   *  A no-op path in the shipped game (only the editor boot calls this). */
  setEditorMode(on: boolean): void {
    if (this.editorMode === on) return;
    this.editorMode = on;
    this.disposeStatic();
    this.buildStaticWorld();
  }

  /** Are the kit GLBs baked and ready to instance? (the editor waits on this before building its prop layer). */
  get kitLoaded(): boolean { return this.kitReady; }

  /** Is EVERY expected kit kind baked (all placeable kinds + the shadow decals)? If a GLB failed (mixed cache /
   *  flaky network) only some kinds bake, and the editor would silently DROP the props of the missing kinds when
   *  it (re)builds the layout — a Save in that state would overwrite the good server rev with a truncated one.
   *  The city editor gates every Save (draft + online) on this. */
  get kitComplete(): boolean {
    if (!this.kitReady) return false;
    for (const k of KIT_KINDS) if (!this.kitBaked.has(k)) return false;
    for (const k of SHADOW_KINDS) if (!this.kitBaked.has(k)) return false;
    return true;
  }
  /** How many of the expected kit kinds actually baked (for the "assets still loading" diagnostic). */
  get kitBakedCount(): number { return this.kitBaked.size; }
  /** How many kit kinds the editor expects when fully loaded (all placeable kinds + shadow decals). */
  get kitExpectedCount(): number { return KIT_KINDS.length + SHADOW_KINDS.size; }

  /** Build ONE individually-transformable prop for `kind` from the baked kit geometry, ground-aligned and
   *  base-scaled EXACTLY as instanceKind() would place it (so the editor matches production at scale 1). The
   *  returned Group's origin sits on the ground at the prop's pivot — the editor then sets
   *  `group.position=(x,0,y)`, `group.rotation.y=-heading`, `group.scale=userScale`. Shares the cached
   *  geometry/material (never disposed). Returns null if the kind isn't baked yet. */
  buildKitProp(kind: string): THREE.Group | null {
    // Onda de-proc — the legacy village dressing (well / crate / old tavern) has no baked GLB: build its dedicated
    // procedural group so the editor can render + transform it exactly as the live scene does (origin at the port
    // pivot, children carrying their world-Y). Scale/rotation are applied by the editor's placeGroup.
    if (LEGACY_PROC_KINDS.has(kind)) {
      if (kind === 'well') return this.buildWellGroup();
      if (kind === 'crate_stack') return this.buildCrateGroup();
      if (kind === 'tavern_old') return this.buildTavernGroup().g;
      return null;
    }
    const baked = this.kitBaked.get(kind);
    if (!baked) return null;
    const sz = baked.box.getSize(new THREE.Vector3());
    const tall = KIT_TALL[kind];
    const scale = tall ? tall / (sz.y || 1) : (KIT_FOOT[kind] ?? 6) / (Math.max(sz.x, sz.z) || 1);
    const mesh = new THREE.Mesh(baked.geo, baked.mat);
    mesh.frustumCulled = false;
    mesh.scale.setScalar(scale);
    mesh.position.y = -baked.box.min.y * scale; // base sits at group-local y=0
    if (SHADOW_KINDS.has(kind)) { mesh.position.y += SHADOW_LIFT; mesh.renderOrder = 2; } // decal floats just over the floor + draws on top
    const g = new THREE.Group();
    g.add(mesh);
    return g;
  }

  /** Draw every render-only ground patch of the active layout (the editor owns these while editing). */
  private buildGround(): void {
    for (const p of this.layout.ground ?? []) {
      const m = this.buildGroundMesh(p.w, p.l, p.mat);
      m.position.set(p.x, 0.03, p.y);
      m.rotation.y = -p.rot;
      this.staticRoot.add(m);
    }
  }

  /** Build ONE ground-patch mesh (origin, flat on y=0), `w`×`l` metres of material `mat`. UVs are baked so the
   *  texture tiles every GROUND_TILE metres at any size. Shared by the renderer and the editor's ground layer. */
  buildGroundMesh(w: number, l: number, mat: number): THREE.Mesh {
    const geo = new THREE.PlaneGeometry(Math.max(0.2, w), Math.max(0.2, l));
    geo.rotateX(-Math.PI / 2);
    const uv = geo.attributes.uv as THREE.BufferAttribute;
    const rx = Math.max(0.2, w) / GROUND_TILE, ry = Math.max(0.2, l) / GROUND_TILE;
    for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * rx, uv.getY(i) * ry);
    uv.needsUpdate = true;
    const texUrl = GROUND_TEX[mat];
    const material = texUrl
      ? new THREE.MeshStandardMaterial({ map: this.tiledTex(texUrl, 1, 1), roughness: 0.96 })
      : new THREE.MeshStandardMaterial({ color: GROUND_TINT[mat] ?? 0x808080, roughness: 1 });
    return new THREE.Mesh(geo, material);
  }

  /* ---------------------- Onda de-proc: the village's legacy procedural dressing (data-driven) ---------------------- */

  /** Render the village's LEGACY procedural props (well / crate stacks / old box tavern) from layout DATA, with
   *  geometry IDENTICAL to the old baked block — so the shipped plaza is byte-identical, but the owner can now
   *  move/delete each in the village editor. Each prop's group is placed at (x, 0, y) and yawed by `-heading`
   *  (the same convention the kit props use), which reproduces the old crate `(cx+cz)*0.3` spin because their
   *  stored heading is that value negated. Live scene only (the editor owns its own selectable prop layer). */
  private buildLegacyProps(props: readonly PortProp[]): void {
    for (const p of props) {
      if (p.kind === 'tavern_old') { this.buildTavern(p.x, p.y, p.heading); continue; }
      let g: THREE.Group | null = null;
      if (p.kind === 'well') g = this.buildWellGroup();
      else if (p.kind === 'crate_stack') g = this.buildCrateGroup();
      if (!g) continue;
      g.position.set(p.x, 0, p.y);
      g.rotation.y = -p.heading;
      this.staticRoot.add(g);
    }
  }

  /** The old central well — a stone drum + a peaked roof on two posts. Origin at port-local (0,0,y); children carry
   *  their exact world-Y so a group placed at (x,0,y) reproduces the old baked well pixel-for-pixel. */
  private buildWellGroup(): THREE.Group {
    const g = new THREE.Group();
    const wellMat = new THREE.MeshStandardMaterial({ color: 0x8f8272, roughness: 1 });
    const roofMat = new THREE.MeshStandardMaterial({ color: 0x5a3a24, roughness: 1 });
    const postMat = new THREE.MeshStandardMaterial({ color: 0x5a3d24, roughness: 1 }); // the shipped `plankMat` tint
    const well = new THREE.Mesh(new THREE.CylinderGeometry(2.4, 2.6, 1.4, 14), wellMat); well.position.y = 0.5; g.add(well);
    const roof = new THREE.Mesh(new THREE.ConeGeometry(2.9, 1.6, 12), roofMat); roof.position.y = 3.0; g.add(roof);
    for (const dx of [-2.2, 2.2]) { const p = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.14, 2.2, 6), postMat); p.position.set(dx, 1.6, 0); g.add(p); }
    return g;
  }

  /** A crate stack — one 1.6 m timber box centred at local (0, 0.7, 0), matching the old baked crate exactly (it
   *  sits 0.1 m into the floor, as it always did). */
  private buildCrateGroup(): THREE.Group {
    const g = new THREE.Group();
    const crate = new THREE.Mesh(new THREE.BoxGeometry(1.6, 1.6, 1.6), new THREE.MeshStandardMaterial({ color: 0x7a5a34, roughness: 1 }));
    crate.position.y = 0.7; g.add(crate);
    return g;
  }

  /** Build the old box-tavern's VISUAL meshes into a group at port-local origin (body/timbers/roof/lit windows/
   *  door/sign) — shared by the live builder (buildTavern) and the editor's prop preview (buildKitProp). Returns
   *  the group + its glowing-window material so the caller can register it for the day/night cycle. NO torches/
   *  porch light here (those are live-scene only). */
  private buildTavernGroup(): { g: THREE.Group; glow: THREE.MeshStandardMaterial } {
    const timber = new THREE.MeshStandardMaterial({ color: 0x4a3320, roughness: 1 });
    const plaster = new THREE.MeshStandardMaterial({ color: 0xd8c49a, roughness: 1 });
    const g = new THREE.Group();
    const body = new THREE.Mesh(new THREE.BoxGeometry(12, 6, 9), plaster); body.position.y = 3; g.add(body);
    for (const [sx, sz] of [[-6, -4.5], [6, -4.5], [-6, 4.5], [6, 4.5]] as const) {
      const t = new THREE.Mesh(new THREE.BoxGeometry(0.6, 6, 0.6), timber); t.position.set(sx, 3, sz); g.add(t);
    }
    const roof = new THREE.Mesh(new THREE.ConeGeometry(9, 3.6, 4), new THREE.MeshStandardMaterial({ color: 0x5a2f22, roughness: 1 }));
    roof.rotation.y = Math.PI / 4; roof.position.y = 7.8; g.add(roof);
    // glowing windows (facing south, -z toward the plaza). Dark by day → warm at night via the day/night cycle.
    const glow = new THREE.MeshStandardMaterial({ color: 0xffcf7a, emissive: 0xffb347, emissiveIntensity: 1.2, roughness: 0.6 });
    for (const wx of [-3.4, 3.4]) { const w = new THREE.Mesh(new THREE.BoxGeometry(1.8, 1.8, 0.3), glow); w.position.set(wx, 3.2, -4.55); g.add(w); }
    const door = new THREE.Mesh(new THREE.BoxGeometry(2.2, 3.2, 0.3), timber); door.position.set(0, 1.6, -4.55); g.add(door);
    const sign = new THREE.Mesh(new THREE.BoxGeometry(2.4, 1.2, 0.2), new THREE.MeshStandardMaterial({ color: 0x2a1c10, roughness: 1 }));
    sign.position.set(-4.2, 3.4, -5.2); g.add(sign);
    return { g, glow };
  }

  /** A chunky, warm-lit tavern facade (procedural — no thin geometry, Sea-of-Thieves timber + glowing windows).
   *  Live scene: positions the shared tavern group, registers its glow for the day/night cycle, and adds the warm
   *  porch light + the two porch torches (yawed with the prop). At heading 0 (the shipped placement) this is
   *  byte-identical to the old baked tavern. */
  private buildTavern(x: number, z: number, heading = 0): void {
    const { g, glow } = this.buildTavernGroup();
    g.position.set(x, 0, z); g.rotation.y = -heading;
    const porch = new THREE.PointLight(0xffb14a, 1.6, 22, 1.6); porch.position.set(0, 2.6, -6); g.add(porch);
    if (!this.editorMode) this.windowMats.push(glow);
    this.staticRoot.add(g);
    // porch torches — the local offsets (±6, -5.2) yawed by the prop so a rotated tavern keeps its lanterns.
    const a = -heading, ca = Math.cos(a), sa = Math.sin(a);
    for (const dx of [-6, 6]) { const dz = -5.2; this.addTorch(x + dx * ca + dz * sa, z - dx * sa + dz * ca, 0.2); }
  }

  private buildProceduralStall(x: number, z: number, heading: number): THREE.Object3D {
    const g = new THREE.Group(); g.position.set(x, 0, z); g.rotation.y = -heading;
    const wood = new THREE.MeshStandardMaterial({ color: 0x6e4a2a, roughness: 1 });
    const counter = new THREE.Mesh(new THREE.BoxGeometry(2.6, 1.0, 1.4), wood); counter.position.y = 0.5; g.add(counter);
    for (const sx of [-1.1, 1.1]) for (const sz of [-0.6, 0.6]) { const p = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.09, 2.4, 6), wood); p.position.set(sx, 1.2, sz); g.add(p); }
    const canopy = new THREE.Mesh(new THREE.BoxGeometry(3.0, 0.2, 1.8), new THREE.MeshStandardMaterial({ color: 0xb03a3a, roughness: 1 }));
    canopy.position.y = 2.5; canopy.rotation.z = 0.08; g.add(canopy);
    g.userData.procedural = true;
    this.staticRoot.add(g);
    return g;
  }

  /** Swap the procedural stalls for the loaded market_stall GLB (village only — the Capital instances its
   *  stalls through the kit path). */
  protected placeStalls(): void {
    if (!this.stallTemplate || this.isCapital) return;
    // remove procedural stalls
    for (const child of [...this.staticRoot.children]) if (child.userData.procedural) this.staticRoot.remove(child);
    const box = new THREE.Box3().setFromObject(this.stallTemplate);
    const size = box.getSize(new THREE.Vector3());
    const scale = 3.2 / (Math.max(size.x, size.z) || 3.2);
    for (const s of this.layout.stalls) {
      const m = this.stallTemplate.clone(true);
      m.scale.setScalar(scale);
      const b2 = new THREE.Box3().setFromObject(m);
      m.position.set(s.x, -b2.min.y, s.y);
      m.rotation.y = -s.heading + Math.PI / 2;
      this.staticRoot.add(m);
      this.addTorch(s.interactX, s.interactY, 0.1); // a lantern at each stall's counter
    }
    this.finalizeTorches(); // rebuild the instanced torches to include the stall lanterns
  }

  /** A tiled ground texture (repeat wrapping, sRGB, mipmapped, moderate anisotropy), cached by url+repeat
   *  so it survives layout rebuilds. Loads async — three.js fills the material's map when the webp lands. */
  protected tiledTex(url: string, rx: number, ry: number): THREE.Texture {
    const key = `${url}:${rx}:${ry}`;
    let tex = this.texCache.get(key);
    if (!tex) {
      tex = this.texLoader.load(url);
      tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
      tex.repeat.set(rx, ry);
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.anisotropy = 4;
      this.texCache.set(key, tex);
    }
    return tex;
  }

  /** Collect a torch placement (built for real by finalizeTorches once the layout is laid out). */
  protected addTorch(x: number, z: number, yBase = 0.2): void {
    this.torchPlaces.push({ x, z, y: yBase });
  }

  /** Bake one iron-tripod BRAZIER geometry (base at y=0): three splayed legs meeting under a fat bowl with
   *  a rim — a chunky, anti-pancake metal brazier, not a thin pole. Merged so every torch shares one mesh. */
  private brazierGeo(): THREE.BufferGeometry {
    const parts: THREE.BufferGeometry[] = [];
    const m = new THREE.Matrix4(), q = new THREE.Quaternion(), up = new THREE.Vector3(0, 1, 0);
    // three splayed legs, 120° apart, feet on a ~0.5 m ring, tops meeting near the bowl
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * Math.PI * 2;
      const leg = new THREE.CylinderGeometry(0.05, 0.07, 1.55, 6);
      const fx = Math.cos(a) * 0.42, fz = Math.sin(a) * 0.42;   // foot offset
      q.setFromUnitVectors(up, new THREE.Vector3(fx, 1.5, fz).normalize()); // lean the leg out to its foot
      m.compose(new THREE.Vector3(fx * 0.5, 0.72, fz * 0.5), q, new THREE.Vector3(1, 1, 1));
      leg.applyMatrix4(m); parts.push(leg);
    }
    const bowl = new THREE.CylinderGeometry(0.4, 0.2, 0.42, 12, 1, true); bowl.translate(0, 1.65, 0); parts.push(bowl);
    const belly = new THREE.SphereGeometry(0.32, 12, 8, 0, Math.PI * 2, Math.PI * 0.5, Math.PI * 0.5); belly.translate(0, 1.46, 0); parts.push(belly);
    const rim = new THREE.TorusGeometry(0.4, 0.055, 6, 16); rim.rotateX(Math.PI / 2); rim.translate(0, 1.86, 0); parts.push(rim);
    return mergeGeometries(parts, false) ?? parts[0]!;
  }

  /** Build the scene's torches: ONE instanced brazier + ONE instanced flame across every collected placement,
   *  plus a warm point light per torch. Rebuilt whenever the torch set changes (e.g. stalls stream in). */
  protected finalizeTorches(): void {
    if (this.torchGroup) { this.staticRoot.remove(this.torchGroup); this.torchGroup = null; }
    this.torchMats.length = 0; this.torchLights.length = 0;
    if (this.torchPlaces.length === 0) return;
    const grp = new THREE.Group(); grp.userData.torches = true;
    const n = this.torchPlaces.length;
    const iron = new THREE.MeshStandardMaterial({ color: 0x35353c, metalness: 0.55, roughness: 0.5 });
    const flameMat = new THREE.MeshStandardMaterial({ color: 0xffb347, emissive: 0xff8c1a, emissiveIntensity: 1.4, roughness: 0.5 });
    const brazier = new THREE.InstancedMesh(this.brazierGeo(), iron, n);
    const flameGeo = new THREE.SphereGeometry(0.24, 8, 8); flameGeo.translate(0, 2.0, 0);
    const flame = new THREE.InstancedMesh(flameGeo, flameMat, n);
    brazier.frustumCulled = false; flame.frustumCulled = false;
    const m4 = new THREE.Matrix4(), qy = new THREE.Quaternion(), up = new THREE.Vector3(0, 1, 0), one = new THREE.Vector3(1, 1, 1), pos = new THREE.Vector3();
    this.torchPlaces.forEach((p, i) => {
      qy.setFromAxisAngle(up, p.x * 0.7 + p.z * 0.3);          // vary yaw so tripods don't all align
      pos.set(p.x, p.y, p.z); m4.compose(pos, qy, one);
      brazier.setMatrixAt(i, m4);
      m4.compose(pos, new THREE.Quaternion(), one); flame.setMatrixAt(i, m4);
      const light = new THREE.PointLight(0xff9d3a, 1.3, 14, 1.8); light.position.set(p.x, p.y + 2.1, p.z);
      light.userData.baseI = 1.3; grp.add(light); this.torchLights.push(light);
    });
    brazier.instanceMatrix.needsUpdate = true; flame.instanceMatrix.needsUpdate = true;
    grp.add(brazier); grp.add(flame);
    this.staticRoot.add(grp);
    this.torchGroup = grp;
    this.torchMats.push(flameMat);
  }

  /* ------------------------------- avatars ------------------------------- */

  /** Onda Avatares: a one-time tweak applied to a skin's SHARED template materials (so every clone inherits it
   *  free). DoubleSide guards against Meshy's single-sided coat/skirt panels dropping back-faces; for the
   *  skeleton (id 2) the base-colour map is reused as a faint emissive map so the bright-painted eye sockets
   *  self-illuminate with an eerie green glow — no per-material hand-picking. */
  private applySkinMaterialTweaks(model: THREE.Object3D, skinId: number): void {
    model.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      const mats = Array.isArray(m.material) ? m.material : [m.material];
      for (const mm of mats) {
        const std = mm as THREE.MeshStandardMaterial;
        std.side = THREE.DoubleSide;
        if (skinId === 2 && std.map) {
          std.emissiveMap = std.map;               // bright-painted bones/eyes self-illuminate; dark coat stays dark
          std.emissive = new THREE.Color(0x4fd8a0);
          std.emissiveIntensity = 0.22;            // subtle spectral tint — an eerie glow, not a uniform green wash
          std.needsUpdate = true;
        }
      }
    });
  }

  /** Build an avatar for (cls, skin): skin>0 picks the cosmetic look (lady/skeleton) if its GLB has landed,
   *  else it falls back to the per-hull captain (recording the WANTED skin + skinReady=false so the preload
   *  tail can upgrade it once the GLB streams in). Skin 0 = the class captain. Capsule fallback if nothing
   *  loaded yet (first-entry race). The walker sim, clips and foot-sync are identical for every look. */
  private makeAvatar(cls: number, skin: number): Avatar {
    const skinTpl = skin > 0 ? this.skinTemplates.get(skin) : undefined;
    const tpl = skinTpl ?? this.templates.get(cls) ?? this.templates.get(ShipClassId.BRIGANTINE) ?? [...this.templates.values()][0];
    const skinReady = skin === 0 ? true : !!skinTpl;
    const group = new THREE.Group();
    const actions = new Map<string, THREE.AnimationAction>();
    let mixer: THREE.AnimationMixer;
    if (tpl) {
      const inst = skeletonClone(tpl.scene);
      inst.scale.setScalar(tpl.scale);
      group.add(inst);
      mixer = new THREE.AnimationMixer(inst);
      for (const clip of tpl.clips) actions.set(clip.name, mixer.clipAction(clip));
      const idle = actions.get('idle'); if (idle) { idle.play(); idle.weight = 1; }
    } else {
      // procedural fallback: a stout capsule body
      const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.4, 1.0, 4, 8), new THREE.MeshStandardMaterial({ color: 0x6b4a8a, roughness: 1 }));
      body.position.y = 0.9; group.add(body);
      const head = new THREE.Mesh(new THREE.SphereGeometry(0.32, 10, 10), new THREE.MeshStandardMaterial({ color: 0xe0b48a, roughness: 1 }));
      head.position.y = 1.7; group.add(head);
      mixer = new THREE.AnimationMixer(group);
    }
    this.scene.add(group);
    return { group, mixer, actions, current: 'idle', emote: null, emoteUntil: 0, cls, skin, skinReady, tag: null, visYaw: NaN, lastX: 0, lastY: 0, hasLast: false, procedural: !tpl, sitting: false, sitY: 0, seatPose: null };
  }

  /** Rebuild an avatar IN PLACE for a new look (a live re-skin from a re-emitted SPAWN, a hull swap, or a
   *  late-loaded GLB), preserving its id, world transform, eased yaw, seated pose and name tag. Disposes the
   *  old group's meshes ONLY when it was the procedural capsule — GLB clones SHARE the template geometry +
   *  materials (skeletonClone), so disposing those would corrupt the template and every other avatar. */
  private swapAvatar(id: number, old: Avatar, cls: number, skin: number): void {
    const fresh = this.makeAvatar(cls, skin);
    if (fresh.procedural && !old.procedural) { this.scene.remove(fresh.group); return; } // don't downgrade to a capsule
    fresh.group.position.copy(old.group.position);
    fresh.group.rotation.y = old.group.rotation.y;
    fresh.visYaw = old.visYaw; fresh.lastX = old.lastX; fresh.lastY = old.lastY; fresh.hasLast = old.hasLast;
    fresh.sitting = old.sitting; fresh.sitY = old.sitY; fresh.seatPose = old.seatPose;
    if (old.tag) { old.group.remove(old.tag); fresh.tag = old.tag; fresh.group.add(old.tag); }
    this.scene.remove(old.group);
    if (old.procedural) old.group.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) { m.geometry?.dispose?.(); const mm = m.material; if (Array.isArray(mm)) mm.forEach((x) => x.dispose?.()); else (mm as THREE.Material)?.dispose?.(); }
    });
    this.avatars.set(id, fresh);
  }

  /** Once the captain / skin GLBs finish streaming in, upgrade any avatar built as the procedural capsule
   *  (first-entry race) OR built off the fallback captain because its skin GLB hadn't landed yet. Preserves
   *  id, world position, facing and name tag. Cosmetic, no wire. */
  private reskinProceduralAvatars(): void {
    if (!this.templates.size && !this.skinTemplates.size) return;
    for (const [id, old] of this.avatars) {
      if (!old.procedural && old.skinReady) continue;
      this.swapAvatar(id, old, old.cls, old.skin);
    }
  }

  private nameTag(name: string, badge = 0): THREE.Sprite {
    // Onda Frota (v47): `badge` (NAME_BADGE bits) paints the name GOLD (gold_name grant) and/or prepends a
    // distinct-colour "DEV" tag, so captains in the plaza / a visited house see the owner's identity.
    const gold = (badge & NAME_BADGE.GOLD) !== 0;
    const dev = (badge & NAME_BADGE.DEV) !== 0;
    const cv = document.createElement('canvas'); cv.width = 256; cv.height = 64;
    const ctx = cv.getContext('2d')!;
    ctx.font = 'bold 30px Georgia, serif'; ctx.textBaseline = 'middle'; ctx.lineJoin = 'round';
    const tag = dev ? 'DEV ' : '';
    const tagW = dev ? ctx.measureText(tag).width : 0;
    const nameW = ctx.measureText(name).width;
    let x = 128 - (tagW + nameW) / 2; // keep the whole label centred
    ctx.textAlign = 'left';
    ctx.lineWidth = 6; ctx.strokeStyle = 'rgba(0,0,0,.85)';
    if (dev) { ctx.strokeText(tag, x, 34); ctx.fillStyle = '#4fc3f7'; ctx.fillText(tag, x, 34); x += tagW; }
    ctx.strokeText(name, x, 34);
    ctx.fillStyle = gold ? '#ffd54a' : '#f2e2b8'; ctx.fillText(name, x, 34);
    const tex = new THREE.CanvasTexture(cv); tex.anisotropy = 2;
    const spr = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true }));
    spr.scale.set(2.4, 0.6, 1); spr.position.y = 2.3;
    return spr;
  }

  /** Crossfade an avatar to a locomotion clip (idle/walk). */
  private setLocomotion(av: Avatar, clip: string): void {
    if (av.current === clip || av.emote) { av.current = clip; return; }
    const next = av.actions.get(clip); const prev = av.actions.get(av.current);
    if (next) { next.reset(); next.setLoop(THREE.LoopRepeat, Infinity); next.play(); next.fadeIn(0.18); }
    if (prev) prev.fadeOut(0.18);
    av.current = clip;
  }

  /** Play an emote clip on an avatar. A one-shot (wave) plays once and returns to locomotion at its clip end;
   *  a looping emote (dance) repeats until cancelled. EITHER is cancelled the instant the avatar moves (handled
   *  in placeAvatar), and re-pressing the SAME looping emote toggles it off. This is the fix for the "stuck in
   *  the pose" bug: the captain GLB emote clips run 5–11 s, so the old one-shot+clamp glued the avatar mid-pose
   *  (sliding, locomotion suppressed) for the whole clip with no way out. */
  playEmote(id: number, emoteId: number): void {
    const av = this.avatars.get(id); if (!av) return;
    if (av.sitting) return;                                   // a seated captain ignores free emotes
    const clip = EMOTE_CLIPS[emoteId]; if (!clip) return;
    const action = av.actions.get(clip); if (!action) return;
    const active = av.emote ? { clip: av.emote, until: av.emoteUntil } : null;
    if (isEmoteToggleOff(emoteId, clip, active)) { this.endEmote(av); return; } // toggle: re-press stops the loop
    const looping = emoteLoops(emoteId);
    // fade out whatever is currently showing — a previous emote, or the locomotion clip
    if (av.emote && av.emote !== clip) { const pe = av.actions.get(av.emote); if (pe) pe.fadeOut(0.15); }
    else if (!av.emote) { const cur = av.actions.get(av.current); if (cur) cur.fadeOut(0.15); }
    action.reset();
    action.setLoop(looping ? THREE.LoopRepeat : THREE.LoopOnce, looping ? Infinity : 1);
    action.clampWhenFinished = !looping;
    action.play(); action.fadeIn(0.12);
    av.emote = clip;
    av.emoteUntil = emoteUntilOf(emoteId, performance.now(), action.getClip().duration * 1000);
  }

  /** Clear an active emote and crossfade back to locomotion next frame. Shared by clip-end, movement-cancel and
   *  toggle. The `current=''` sentinel forces setLocomotion to re-play a fresh idle/walk (its action was faded
   *  out when the emote began), so there is no T-pose or frozen pop. */
  private endEmote(av: Avatar): void {
    if (!av.emote) return;
    const e = av.actions.get(av.emote); if (e) e.fadeOut(0.18);
    av.emote = null; av.emoteUntil = 0; av.current = '';
  }

  /** Onda 8 Interior Vivo — hold (or release) an avatar in a seated pose using the captain GLB's `sit` clip.
   *  `seat` lifts + (for a REMOTE) snaps the avatar onto the seat surface. Drives BOTH the local sitter and,
   *  v43, remote sitters replicated off the wire. Walking releases the pose (caller-detected for the local;
   *  a SIT(off) event for remotes). */
  setSit(id: number, on: boolean, seat?: { x: number; y: number; heading: number; y0: number }): void {
    const av = this.avatars.get(id); if (!av) return;
    if (on && seat) av.sitY = seat.y0;
    av.seatPose = on && seat ? { x: seat.x, y: seat.y, heading: seat.heading } : null;
    if (on === av.sitting) return;
    av.sitting = on;
    const sit = av.actions.get('sit');
    if (on) {
      if (av.emote) { const e = av.actions.get(av.emote); if (e) e.fadeOut(0.2); av.emote = null; }
      const prev = av.actions.get(av.current); if (prev) prev.fadeOut(0.25);
      if (sit) { sit.reset(); sit.setLoop(THREE.LoopOnce, 1); sit.clampWhenFinished = true; sit.play(); sit.fadeIn(0.28); }
    } else {
      if (sit) sit.fadeOut(0.28);
      const back = av.actions.get(av.current); if (back) { back.reset(); back.play(); back.fadeIn(0.25); }
    }
  }

  /* ------------------------- Onda Cidade Viva — Sit on a plaza bench (local visual) ------------------------- */

  /** The seat transform of the nearest sittable CITY prop (the cobble bench) within reach of a port-local point,
   *  or null. The geometry is the pure `nearestPortSeat` in @piratas/sim (unit-tested there) so the picker can
   *  never drift from the placed-prop data — the seated pose reads straight off the bench that was decorated in. */
  citySittableAt(x: number, y: number): SeatSpot | null {
    return nearestPortSeat((this.layout.props ?? []) as readonly CityProp[], x, y);
  }

  /** Act on a Sit press at the captain's current plaza position (mirrors HouseScene.toggleSit):
   *  - already seated → stand (`'stood'`); a bench within reach → sit, snapping to it (`'sat'`); else `'noseat'`.
   *  v43: sitting/standing fires onSitWire so the server re-broadcasts the pose to every observer. */
  toggleCitySit(x: number, y: number): 'sat' | 'stood' | 'noseat' {
    if (this._portSit) { this.standCity(); return 'stood'; }
    const seat = this.citySittableAt(x, y);
    if (!seat) return 'noseat';
    this._portSeat = seat; this._portSitFrom = { x, y }; this._portSit = true;
    if (this._sitYourId >= 0) this.setSit(this._sitYourId, true, seat);
    this.onSitWire?.(true);
    return 'sat';
  }

  private standCity(): void {
    if (!this._portSit) return;
    this._portSit = false; this._portSeat = null; this._portSitFrom = null;
    if (this._sitYourId >= 0) this.setSit(this._sitYourId, false);
    this.onSitWire?.(false);
  }

  /** v43 Synced Sit — replicate a REMOTE captain's seated/stood pose received off the wire. Snaps them onto the
   *  server-derived seat (so their drifting walker position never floats them off the bench). Self is skipped by
   *  the caller (it owns its sit locally). Safe if the avatar isn't ensured yet — setSit no-ops until it is. */
  applyRemoteSit(e: { id: number; on: number; x: number; y: number; heading: number; y0: number }): void {
    if (e.on) this.setSit(e.id, true, { x: e.x, y: e.y, heading: e.heading, y0: e.y0 });
    else this.setSit(e.id, false);
  }

  isCitySitting(): boolean { return this._portSit; }

  /** Onda Sit-Âncora — the City editor's live Seat preview: pin the ghost avatar into `spot` (an already-anchored
   *  SeatSpot), or release it with null. Bypasses nearestPortSeat + the walk-away auto-stand — inert in-game. */
  editorPreviewSit(spot: SeatSpot | null): void {
    this._editorSit = spot;
    if (!spot && this._sitYourId >= 0) this.setSit(this._sitYourId, false);
  }

  /* ------------------------------- Onda NPC Mercante ------------------------------- */

  /** Place (or replace) the city's market NPC for the port just entered. Keyed on the 1-based outpost id so
   *  every client sees the same merchant, and two villages that share PORT_LAYOUT still get different ones.
   *  Deferred until the captain GLBs finish streaming in (built at the tail of preload()). Client-only, no wire. */
  setMerchant(outpostId: number): void {
    if (this.merchant && this.merchantOutpost === outpostId) return;
    this.pendingMerchantOutpost = outpostId;
    if (this.templates.size) this.buildMerchant(outpostId);
  }

  /** Remove the merchant (leaving the port). Disposes ONLY the resources we own — the cloned tint materials,
   *  the procedural wares, and the name-tag texture. The skinned-mesh GEOMETRY is shared with the template, so
   *  (like the avatars) it is never disposed here. */
  clearMerchant(): void {
    this.pendingMerchantOutpost = null;
    this.merchantOutpost = -1;
    const mch = this.merchant;
    if (!mch) return;
    this.merchant = null;
    this.scene.remove(mch.root);
    for (const mat of mch.mats) mat.dispose?.();
    mch.wares.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) { m.geometry?.dispose?.(); const mm = m.material; if (Array.isArray(mm)) mm.forEach((x) => x.dispose?.()); else mm?.dispose?.(); }
    });
    (mch.tag.material.map as THREE.Texture | null)?.dispose?.();
    mch.tag.material.dispose();
  }

  /** Build the merchant for `outpostId`: seed the name/tint/base-hull deterministically, clone a captain,
   *  recolor its materials toward an earthy merchant tone, hold its `idle` clip, and anchor it just behind the
   *  layout's plaza-facing stall (facing the customer spot). */
  private buildMerchant(outpostId: number): void {
    const stalls = this.layout.stalls;
    if (!stalls.length) return;
    this.clearMerchant();
    const rand = mulberry32((outpostId * 2654435761) >>> 0);
    const name = MERCHANT_NAMES[Math.floor(rand() * MERCHANT_NAMES.length)]!;
    const tint = MERCHANT_TINTS[Math.floor(rand() * MERCHANT_TINTS.length)]!;
    const cls = Math.floor(rand() * 4); // SLOOP..FRIGATE — the base captain GLB this merchant wears
    const stallIdx = 0;                 // the first (plaza-facing) stall of the layout is the merchant's counter
    const s = stalls[stallIdx]!;
    // anchor: just behind + slightly beside the counter, facing the interact point (the customer's spot)
    let fx = s.interactX - s.x, fy = s.interactY - s.y; // "front" — toward a trading captain
    const fl = Math.hypot(fx, fy) || 1; fx /= fl; fy /= fl;
    const px = -fy, py = fx;                              // lateral to the counter
    const homeX = s.x - fx * 1.4 + px * 0.7;
    const homeY = s.y - fy * 1.4 + py * 0.7;
    const baseYaw = Math.atan2(fy, fx);                  // sim heading facing the customer / plaza flow

    const tpl = this.templates.get(cls) ?? this.templates.get(ShipClassId.BRIGANTINE) ?? [...this.templates.values()][0];
    if (!tpl) return;
    const root = new THREE.Group(); root.position.set(homeX, 0, homeY);
    const avatarPivot = new THREE.Group();
    const inst = skeletonClone(tpl.scene);
    inst.scale.setScalar(tpl.scale);
    const mats: THREE.Material[] = [];
    const tintCol = new THREE.Color(tint);
    inst.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      m.frustumCulled = false; m.castShadow = false; m.receiveShadow = false;
      const src = Array.isArray(m.material) ? m.material : [m.material];
      const cloned = src.map((mm) => { const c = (mm as THREE.MeshStandardMaterial).clone(); if (c.color) c.color.lerp(tintCol, MERCHANT_TINT_MIX); mats.push(c); return c; });
      m.material = Array.isArray(m.material) ? cloned : cloned[0]!;
    });
    avatarPivot.add(inst);
    avatarPivot.rotation.y = MODEL_YAW - baseYaw;
    root.add(avatarPivot);
    // hold the idle clip (the standing loop) — falls back to a frozen first frame if a GLB lacks 'idle'
    const mixer = new THREE.AnimationMixer(inst);
    const idle = tpl.clips.find((c) => c.name === 'idle');
    if (idle) { const a = mixer.clipAction(idle); a.play(); a.weight = 1; }
    else if (tpl.clips[0]) { const a = mixer.clipAction(tpl.clips[0]); a.play(); a.paused = true; }
    // the merchant's wares beside the counter
    const wares = this.buildWares();
    root.add(wares);
    // floating gold name tag
    const tag = this.merchantTag(`🪙 ${name} — Merchant`);
    root.add(tag);
    this.scene.add(root);
    this.merchant = { root, avatarPivot, wares, mixer, tag, mats, name, stallIdx, homeX, homeY, baseYaw, visYaw: MODEL_YAW - baseYaw };
    this.merchantOutpost = outpostId;
    this.pendingMerchantOutpost = null;
  }

  /** A cheap procedural cluster of wares (crate + barrel + grain sack) set beside the merchant's counter. */
  private buildWares(): THREE.Group {
    const g = new THREE.Group();
    const wood = new THREE.MeshStandardMaterial({ color: 0x7a5a34, roughness: 1 });
    const iron = new THREE.MeshStandardMaterial({ color: 0x5c5140, roughness: 0.8, metalness: 0.2 });
    const cloth = new THREE.MeshStandardMaterial({ color: 0xb39a6a, roughness: 1 });
    const crate = new THREE.Mesh(new THREE.BoxGeometry(1.1, 1.1, 1.1), wood); crate.position.set(1.5, 0.55, -0.7); crate.rotation.y = 0.4; g.add(crate);
    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.55, 1.15, 12), iron); barrel.position.set(1.8, 0.58, 0.6); g.add(barrel);
    const sack = new THREE.Mesh(new THREE.SphereGeometry(0.42, 8, 8), cloth); sack.position.set(1.0, 0.4, 1.0); sack.scale.set(1, 0.85, 1); g.add(sack);
    return g;
  }

  /** The merchant's floating name tag (a wider, gold-inked variant of the avatar tag for the longer label). */
  private merchantTag(text: string): THREE.Sprite {
    const cv = document.createElement('canvas'); cv.width = 512; cv.height = 64;
    const ctx = cv.getContext('2d')!;
    ctx.font = 'bold 30px Georgia, serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.lineWidth = 6; ctx.strokeStyle = 'rgba(0,0,0,.85)'; ctx.strokeText(text, 256, 34);
    ctx.fillStyle = '#ffd98a'; ctx.fillText(text, 256, 34);
    const tex = new THREE.CanvasTexture(cv); tex.anisotropy = 2;
    const spr = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true }));
    spr.scale.set(4.0, 0.5, 1); spr.position.y = 2.35;
    return spr;
  }

  /** Per-frame: advance the idle clip and ease the merchant's yaw to face the nearest captain within
   *  MERCHANT_NOTICE_R (a gentle head/torso turn), otherwise ease back to the plaza-facing base yaw. Cheap. */
  private updateMerchant(local: { x: number; y: number } | null, remotes: { x: number; y: number }[], dt: number): void {
    const mch = this.merchant; if (!mch) return;
    mch.mixer.update(dt);
    let bestD = MERCHANT_NOTICE_R * MERCHANT_NOTICE_R, tx = NaN, ty = NaN;
    const consider = (x: number, y: number): void => { const d = (x - mch.homeX) ** 2 + (y - mch.homeY) ** 2; if (d < bestD) { bestD = d; tx = x; ty = y; } };
    if (local) consider(local.x, local.y);
    for (const r of remotes) consider(r.x, r.y);
    const heading = Number.isNaN(tx) ? mch.baseYaw : Math.atan2(ty - mch.homeY, tx - mch.homeX);
    const targetYaw = MODEL_YAW - heading;
    let dz = targetYaw - mch.visYaw; while (dz > Math.PI) dz -= 2 * Math.PI; while (dz < -Math.PI) dz += 2 * Math.PI;
    mch.visYaw += dz * (1 - Math.pow(0.0016, dt)); // gentle ~0.2s turn — never a snap
    mch.avatarPivot.rotation.y = mch.visYaw;
  }

  /** The diegetic prompt for a stall: the merchant's own line at the stall it tends, else null so the caller
   *  keeps the generic stall prompt. */
  merchantHintFor(stallIdx: number): string | null {
    return this.merchant && this.merchant.stallIdx === stallIdx ? `🪙 Trade with ${this.merchant.name}` : null;
  }

  /* ------------------------------- Onda Dealers ------------------------------- */

  /** Thread the audio engine in (once, at setup) so a dealer can play its pre-generated voice clips. */
  setDealerAudio(audio: Audio): void { this.audio = audio; }

  /** Place croupiers for the blackjack + poker tables of the port just entered (keyed on the outpost id so every
   *  client sees the same dealers). Deferred until the captain GLBs stream in (built at the tail of preload()).
   *  Client-only, no wire. A layout with no casino (a village) builds none. */
  setDealers(outpostId: number): void {
    const hasTables = (this.layout.casino ?? []).some((s) => s.game === CasinoGame.BLACKJACK || s.game === CasinoGame.POKER);
    if (!hasTables) { this.clearDealers(); return; } // a village / a casino-less layout gets no dealers (and no voice fetch)
    if (this.dealersOutpost === outpostId && this.dealers.length) return;
    this.pendingDealersOutpost = outpostId;
    if (this.audio) this.audio.ensureDealerVoices(DEALER_CLIP_IDS); // lazy-load the voice bank on first casino entry
    if (this.templates.size) this.buildDealers(outpostId);
  }

  /** Remove the dealers (leaving the port). Disposes only what we own — the cloned tint materials + the two
   *  sprite textures per dealer (the skinned-mesh geometry is shared with the template, so never disposed here). */
  clearDealers(): void {
    this.pendingDealersOutpost = null;
    this.dealersOutpost = -1;
    for (const d of this.dealers) {
      this.scene.remove(d.root);
      for (const m of d.mats) m.dispose?.();
      for (const spr of [d.nameTag, d.bubble]) { (spr.material.map as THREE.Texture | null)?.dispose?.(); spr.material.dispose(); }
    }
    this.dealers = [];
  }

  /** Build a croupier behind each blackjack + poker station of the applied layout. Each is seeded deterministically
   *  from (outpostId, stationId): a name from its pit's pool, a gambler-black tint, a base captain GLB. Anchored
   *  just behind the table (opposite the player's interact spot) and facing the felt. */
  private buildDealers(outpostId: number): void {
    this.clearDealers();
    const stations = this.layout.casino ?? [];
    const plans = planDealers(outpostId, stations, mulberry32, CasinoGame.BLACKJACK, CasinoGame.POKER);
    for (const plan of plans) {
      const st = stations.find((s) => s.id === plan.stationId)!;
      const voice = plan.voice;
      const rand = mulberry32(((outpostId * 2654435761) ^ (st.id * 40503) ^ (st.game * 2246822519) ^ 0x9e3779b9) >>> 0); // line/interval rolls (distinct from the plan seed)
      const name = plan.name;
      const tint = DEALER_TINTS[plan.tintIdx % DEALER_TINTS.length]!;
      const cls = plan.clsRoll; // SLOOP..FRIGATE — the base captain the dealer wears
      // "front" points from the table toward the interact spot (where the captain stands) → the dealer faces that.
      let fx = st.interactX - st.x, fy = st.interactY - st.y; const fl = Math.hypot(fx, fy) || 1; fx /= fl; fy /= fl;
      const homeX = st.x - fx * 1.15, homeY = st.y - fy * 1.15; // just behind the felt, opposite the player
      const baseYaw = Math.atan2(fy, fx);

      const tpl = this.templates.get(cls) ?? this.templates.get(ShipClassId.BRIGANTINE) ?? [...this.templates.values()][0];
      if (!tpl) return;
      const root = new THREE.Group(); root.position.set(homeX, 0, homeY);
      const avatarPivot = new THREE.Group();
      const inst = skeletonClone(tpl.scene);
      inst.scale.setScalar(tpl.scale);
      const mats: THREE.Material[] = [];
      const tintCol = new THREE.Color(tint);
      inst.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh) return;
        m.frustumCulled = false; m.castShadow = false; m.receiveShadow = false;
        const src = Array.isArray(m.material) ? m.material : [m.material];
        const cloned = src.map((mm) => { const c = (mm as THREE.MeshStandardMaterial).clone(); if (c.color) c.color.lerp(tintCol, DEALER_TINT_MIX); mats.push(c); return c; });
        m.material = Array.isArray(m.material) ? cloned : cloned[0]!;
      });
      avatarPivot.add(inst);
      avatarPivot.rotation.y = MODEL_YAW - baseYaw;
      root.add(avatarPivot);
      const mixer = new THREE.AnimationMixer(inst);
      const idle = tpl.clips.find((c) => c.name === 'idle');
      if (idle) { const a = mixer.clipAction(idle); a.play(); a.weight = 1; }
      else if (tpl.clips[0]) { const a = mixer.clipAction(tpl.clips[0]); a.play(); a.paused = true; }
      const icon = voice === 'poker' ? '🃏' : '🎲';
      const nameTag = this.dealerLabel(`${icon} ${name} — Dealer`, '#ffd98a', 34);
      root.add(nameTag);
      const bubble = this.dealerLabel('', '#fff4dc', 30); bubble.position.y = 2.95; bubble.visible = false;
      root.add(bubble);
      this.scene.add(root);
      const now = performance.now();
      this.dealers.push({
        root, avatarPivot, mixer, nameTag, bubble, mats, name, voice, stationId: st.id,
        homeX, homeY, worldX: homeX, worldZ: homeY, baseYaw, visYaw: MODEL_YAW - baseYaw,
        rand, nextIdleAt: now + DEALER_IDLE_MIN_MS + rand() * (DEALER_IDLE_MAX_MS - DEALER_IDLE_MIN_MS),
        lastLineAt: 0, bubbleUntil: 0,
      });
    }
    this.dealersOutpost = outpostId;
    this.pendingDealersOutpost = null;
  }

  /** A floating label sprite (name tag / speech balloon) — a wider gold or cream text plate above the dealer. */
  private dealerLabel(text: string, fill: string, px: number): THREE.Sprite {
    const spr = new THREE.Sprite(new THREE.SpriteMaterial({ depthTest: false, transparent: true }));
    spr.scale.set(4.2, 0.55, 1); spr.position.y = 2.35;
    this.paintDealerLabel(spr, text, fill, px);
    return spr;
  }
  /** (Re)paint a label sprite's texture. Called on build + whenever a balloon shows a new line. */
  private paintDealerLabel(spr: THREE.Sprite, text: string, fill: string, px: number): void {
    const cv = document.createElement('canvas'); cv.width = 512; cv.height = 72;
    const ctx = cv.getContext('2d')!;
    ctx.font = `bold ${px}px Georgia, serif`; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.lineWidth = 6; ctx.strokeStyle = 'rgba(0,0,0,.85)'; ctx.strokeText(text, 256, 38);
    ctx.fillStyle = fill; ctx.fillText(text, 256, 38);
    const tex = new THREE.CanvasTexture(cv); tex.anisotropy = 2;
    (spr.material.map as THREE.Texture | null)?.dispose?.();
    spr.material.map = tex; spr.material.needsUpdate = true;
  }

  /** Per-frame: advance each dealer's idle clip, ease its yaw to face the nearest captain within DEALER_NOTICE_R,
   *  hide an expired balloon, and — while a captain is standing at the felt — fire the odd idle mutter. Cheap. */
  private updateDealers(local: { x: number; y: number } | null, remotes: { x: number; y: number }[], dt: number): void {
    if (!this.dealers.length) return;
    this.audio?.ensureDealerVoices(DEALER_CLIP_IDS); // cheap once-guarded: (re)tries the lazy load until the ctx exists
    const now = performance.now();
    for (const d of this.dealers) {
      d.mixer.update(dt);
      // nearest captain (local + interpolated remotes) to this dealer's table
      let bestD = DEALER_NOTICE_R * DEALER_NOTICE_R, tx = NaN, ty = NaN;
      const consider = (x: number, y: number): void => { const dd = (x - d.homeX) ** 2 + (y - d.homeY) ** 2; if (dd < bestD) { bestD = dd; tx = x; ty = y; } };
      if (local) consider(local.x, local.y);
      for (const r of remotes) consider(r.x, r.y);
      const heading = Number.isNaN(tx) ? d.baseYaw : Math.atan2(ty - d.homeY, tx - d.homeX);
      const targetYaw = MODEL_YAW - heading;
      let dz = targetYaw - d.visYaw; while (dz > Math.PI) dz -= 2 * Math.PI; while (dz < -Math.PI) dz += 2 * Math.PI;
      d.visYaw += dz * (1 - Math.pow(0.0016, dt));
      d.avatarPivot.rotation.y = d.visYaw;
      if (d.bubbleUntil && now >= d.bubbleUntil) { d.bubble.visible = false; d.bubbleUntil = 0; }
      // idle mutter: only when a captain is actually at the felt, and never more than the per-table cap
      if (!Number.isNaN(tx) && now >= d.nextIdleAt) {
        this.dealerSpeak(d, 'idle', now);
        d.nextIdleAt = now + DEALER_IDLE_MIN_MS + d.rand() * (DEALER_IDLE_MAX_MS - DEALER_IDLE_MIN_MS);
      }
    }
  }

  /** React to a table event at `stationId` (a big win, a natural blackjack, an all-in) — the matching dealer
   *  throws a themed line, subject to the same 1/20 s-per-table cap. Called from main.ts off the casino results. */
  dealerReact(stationId: number, kind: DealerLineKind): void {
    const d = this.dealers.find((x) => x.stationId === stationId);
    if (d) this.dealerSpeak(d, kind, performance.now());
  }

  /** Fire one line: enforce the per-table cadence cap, pick a deterministic line for the dealer's voice+kind,
   *  show the balloon, and play the pre-generated voice clip (positional, on the sfx bus, mute-respecting). */
  private dealerSpeak(d: Dealer, kind: DealerLineKind, now: number): void {
    if (now - d.lastLineAt < DEALER_LINE_MIN_MS) return; // hard cap: at most one line per table per 20 s
    const line = pickDealerLine(d.voice, kind, Math.floor(d.rand() * 0x7fffffff));
    if (!line) return;
    d.lastLineAt = now;
    this.paintDealerLabel(d.bubble, line.text, '#fff4dc', 30);
    d.bubble.visible = true; d.bubbleUntil = now + DEALER_BUBBLE_MS;
    // cheap positional pan: project the dealer's world position to NDC-x relative to the current camera
    let pan = 0;
    try { const v = new THREE.Vector3(d.worldX, 1.8, d.worldZ).project(this.camera); pan = Math.max(-1, Math.min(1, v.x)); } catch { /* pre-camera */ }
    this.audio?.dealerVoice(line.id, pan);
  }

  /* ------------------------------- per-frame ------------------------------- */

  reset(): void {
    this.standCity(); // drop any seated pose so re-entering / leaving a port never keeps a ghost sit
    for (const av of this.avatars.values()) this.scene.remove(av.group);
    this.avatars.clear();
  }

  /**
   * Sync + render one frame. `local` is the predicted local avatar body; `remotes` the interpolated
   * others (from net.getInterpAvatars); `names`/`cls` come from the spawn caches.
   */
  sync(
    local: { x: number; y: number; heading: number; moving: boolean; cls: number; skin: number } | null,
    yourId: number,
    remotes: { id: number; x: number; y: number; heading: number; moving: boolean; cls: number; skin: number; name: string; badge?: number }[],
    t: number, dt: number,
  ): void {
    const seen = new Set<number>();
    this._sitYourId = yourId;
    // Onda Cidade Viva — a seated captain (on a plaza bench) is snapped to the seat; walking away releases it.
    // Inert in a house (HouseScene owns its own sit + passes an already-snapped `local`), so `self === local` there.
    let self = local;
    if (this._editorSit) {
      // City editor Seat preview: pin the ghost onto the configured (anchored) seat, whatever `local` is.
      const s = this._editorSit;
      self = { x: s.x, y: s.y, heading: s.heading, moving: false, cls: local?.cls ?? 0, skin: local?.skin ?? 0 };
    } else if (this._portSit && local) {
      if (this._portSitFrom) { const dx = local.x - this._portSitFrom.x, dy = local.y - this._portSitFrom.y; if (dx * dx + dy * dy > 0.6 * 0.6) this.standCity(); }
      if (this._portSit && this._portSeat) self = { x: this._portSeat.x, y: this._portSeat.y, heading: this._portSeat.heading, moving: false, cls: local.cls, skin: local.skin };
    }

    // local avatar
    if (self) {
      seen.add(yourId);
      const av = this.ensureAvatar(yourId, self.cls, self.skin, 'You');
      if (this._editorSit) this.setSit(yourId, true, this._editorSit); // pin the sit AFTER the avatar exists (frame 1)
      this.placeAvatar(av, self.x, self.y, self.heading, self.moving, dt);
    }
    // remotes
    for (const r of remotes) {
      seen.add(r.id);
      const av = this.ensureAvatar(r.id, r.cls, r.skin, r.name, r.badge ?? 0);
      this.placeAvatar(av, r.x, r.y, r.heading, r.moving, dt);
    }
    // reap avatars that left the plaza
    for (const [id, av] of this.avatars) if (!seen.has(id)) { this.scene.remove(av.group); this.avatars.delete(id); }

    // Onda Cidade Noturna — retint the scene by time-of-day, then flame flicker (boosted the darker it gets)
    const torchBoost = this.applyCityTime();
    const fl = 1.2 + Math.sin(t * 9) * 0.25 + Math.sin(t * 21) * 0.12;
    for (const m of this.torchMats) m.emissiveIntensity = fl * torchBoost;

    // Onda NPC Mercante — idle + turn to face the nearest captain (local + interpolated remotes)
    if (this.merchant) this.updateMerchant(self, remotes, dt);
    // Onda Dealers — croupiers idle, face the felt's captain, and mutter the odd deadpan line
    this.updateDealers(self, remotes, dt);

    // camera: third-person behind the local avatar (overridable so HouseScene can lift to an iso
    // decorate view without touching the port's follow behavior)
    if (self) this.updateFollowCamera(self, dt);
    this.updatePortLod(); // hero-building LOD swap for the current camera distance
    this.render();
  }

  /** Third-person follow camera behind the local avatar. Onda 8: HouseScene overrides this to raise into
   *  an isometric decorate view while editing (behavior-preserving extraction — the port path is unchanged). */
  protected updateFollowCamera(local: { x: number; y: number; heading: number }, dt: number): void {
    const camDist = 7.5, camH = 4.2;
    // Trail from behind the DECOUPLED view azimuth (NOT the avatar heading) — so facing your movement can't
    // rotate the camera. Only the camera POSITION eases toward the avatar; the azimuth is player-owned (camYaw).
    const fx = Math.cos(this.camYaw), fy = Math.sin(this.camYaw);
    const wantX = local.x - fx * camDist;
    const wantZ = local.y - fy * camDist;
    const k = 1 - Math.pow(0.001, dt); // smooth positional follow (azimuth never chases heading → no feedback loop)
    this.camPos.x += (wantX - this.camPos.x) * k;
    this.camPos.z += (wantZ - this.camPos.z) * k;
    this.camPos.y += (camH - this.camPos.y) * k;
    this.camTarget.x += (local.x - this.camTarget.x) * k;
    this.camTarget.z += (local.y - this.camTarget.z) * k;
    this.camTarget.y = 1.4;
    this.camera.position.copy(this.camPos);
    this.camera.lookAt(this.camTarget);
  }

  /** The camera's ground-plane forward as a unit sim-plane vector (x→east, y→north), for CAMERA-RELATIVE walk
   *  input (WoW/L2 feel: forward advances where the camera looks, strafe goes to its right). Returned straight
   *  from the DECOUPLED `camYaw` (not read back from the eased camera transform) so it is rock-stable every frame
   *  — the value main.ts rotates its intent by can't wobble or spiral. The wire + shared walker sim stay
   *  untouched (prediction == server). */
  groundForward(): { x: number; y: number } {
    return { x: Math.cos(this.camYaw), y: Math.sin(this.camYaw) };
  }

  private ensureAvatar(id: number, cls: number, skin: number, name: string, badge = 0): Avatar {
    let av = this.avatars.get(id);
    if (!av) {
      av = this.makeAvatar(cls, skin);
      if (name) { av.tag = this.nameTag(name, badge); av.group.add(av.tag); }
      this.avatars.set(id, av);
    } else if (av.skin !== skin || av.cls !== cls) {
      // Onda Avatares: a live re-skin (re-emitted SPAWN) or a hull swap — rebuild the model, keep tag+transform.
      this.swapAvatar(id, av, cls, skin);
      av = this.avatars.get(id)!;
    }
    return av;
  }

  private placeAvatar(av: Avatar, x: number, y: number, heading: number, moving: boolean, dt: number): void {
    // Onda 8 Interior Vivo: a seated avatar holds its `sit` pose — lifted onto the seat surface, yaw eased to
    // the seat facing. Locomotion/emote decisions are skipped so the pose never flickers back to idle/walk.
    // v43: a REMOTE sitter reads its transform off the server-derived seat (seatPose) — its interpolated walker
    // position drifts off the bench — while the local sitter (seatPose null) uses the already-snapped transform.
    if (av.sitting) {
      const sx = av.seatPose ? av.seatPose.x : x, sy = av.seatPose ? av.seatPose.y : y;
      const shead = av.seatPose ? av.seatPose.heading : heading;
      av.group.position.set(sx, av.sitY, sy);
      const targetYaw = MODEL_YAW - shead;
      if (Number.isNaN(av.visYaw)) av.visYaw = targetYaw;
      let dzs = targetYaw - av.visYaw; while (dzs > Math.PI) dzs -= 2 * Math.PI; while (dzs < -Math.PI) dzs += 2 * Math.PI;
      av.visYaw += dzs * (1 - Math.pow(0.0008, dt));
      av.group.rotation.y = av.visYaw;
      av.lastX = x; av.lastY = y; av.hasLast = true;
      av.mixer.update(dt);
      return;
    }
    av.group.position.set(x, 0, y);
    // Fix 11: MEASURE the real ground speed from the position delta (works for the predicted local avatar AND
    // interpolated remotes) — this drives both the walk/idle decision and the clip's playback rate.
    let speed = 0;
    if (av.hasLast && dt > 1e-4) speed = Math.hypot(x - av.lastX, y - av.lastY) / dt;
    av.lastX = x; av.lastY = y; av.hasLast = true;
    // Fix 11: ease the RENDER yaw toward the heading (angular approach) instead of snapping — a smooth,
    // MMO-style turn. Visual only; the deterministic walker heading is untouched.
    const targetYaw = MODEL_YAW - heading;
    if (Number.isNaN(av.visYaw)) av.visYaw = targetYaw;
    let dz = targetYaw - av.visYaw; while (dz > Math.PI) dz -= 2 * Math.PI; while (dz < -Math.PI) dz += 2 * Math.PI;
    av.visYaw += dz * (1 - Math.pow(0.0008, dt)); // ~0.15s time constant → snappy but never a snap
    av.group.rotation.y = av.visYaw;
    // Emote lifecycle (pure decision in emotefsm): a one-shot (wave) ends at its clip time; a loop (dance) runs
    // forever — but ANY real movement cancels BOTH, so the pose can never stick while the captain walks off (the
    // reported bug). `moving` is the authoritative walk signal (local: predicted velocity; remotes: the server
    // MOVING flag); measured `speed` is a backup. Toggle re-press is handled in playEmote.
    if (av.emote && shouldEndEmote({ clip: av.emote, until: av.emoteUntil }, moving, speed, performance.now())) this.endEmote(av);
    if (!av.emote) {
      // hysteresis on the measured speed so a stroll near the threshold never flickers idle↔walk
      const wasWalk = av.current === 'walk';
      const walking = speed > (wasWalk ? 0.18 : 0.55) || (moving && speed > 0.12);
      this.setLocomotion(av, walking ? 'walk' : 'idle');
      // foot-sync: play the walk clip at the ground speed so the feet plant instead of sliding
      const walk = av.actions.get('walk');
      if (walk) walk.timeScale = walking ? Math.max(0.35, Math.min(2.2, speed / WALK_CLIP_STRIDE)) : 1;
    }
    av.mixer.update(dt);
  }

  private render(): void {
    if (this.webgl.getContext().isContextLost()) return; // WKWebView OOM: the GL context is gone — the reload card is up
    const w = window.innerWidth, h = window.innerHeight;
    if (this.camera.aspect !== w / h) { this.camera.aspect = w / Math.max(1, h); this.camera.updateProjectionMatrix(); }
    this.webgl.render(this.scene, this.camera);
  }

  /* ------------------------------- helpers ------------------------------- */

  /** The nearest stall index within interact range of a port-local point, or -1. */
  nearestStall(x: number, y: number): number {
    let best = -1, bestD = PORT.INTERACT_R * PORT.INTERACT_R;
    this.layout.stalls.forEach((s, i) => {
      const d = (x - s.interactX) ** 2 + (y - s.interactY) ** 2;
      if (d < bestD) { bestD = d; best = i; }
    });
    return best;
  }

  /** The nearest casino station (poker/slots/blackjack/auction/clan-hall) within interact range, or null.
   *  Onda 7: the client detects proximity exactly like a stall and opens the matching UI. */
  nearestStation(x: number, y: number): PortStation | null {
    let best: PortStation | null = null, bestD = CASINO.INTERACT_R * CASINO.INTERACT_R;
    for (const s of this.layout.casino ?? []) {
      const d = (x - s.interactX) ** 2 + (y - s.interactY) ** 2;
      if (d < bestD) { bestD = d; best = s; }
    }
    return best;
  }

  /** Is a port-local point on/near the exit dock of the active layout (village or capital)? */
  atExit(x: number, y: number): boolean {
    const e = this.layout.exit;
    return y <= e.maxY + 2.5 && x >= e.minX - 2 && x <= e.maxX + 2;
  }

  /** Port-local distance-squared to the tavern centre (for the audio crossfade). */
  tavernProximity(x: number, y: number): number {
    const t = this.layout.tavern;
    const d = Math.hypot(x - t.x, y - t.y);
    const inner = t.r, outer = t.r + PORT.TAVERN_AUDIO_R;
    return Math.max(0, Math.min(1, 1 - (d - inner) / (outer - inner)));
  }
}
